import { AuditTable } from './dossier/AuditTable'
import { RecalculationNote } from './dossier/RecalculationNote'
import './dossier/dossier-polish.css'
import './dossier/dossier-theme.css'
/**
 * Deal Dossier — Property Dossier layout for a live deal record.
 *
 * Tabs from the previous chrome map onto sections of this layout (Record,
 * Property Profile, Sponsor, Source, Screening, Narratives, Activity).
 * Values come from the loan row; missing fields say so. Nothing is invented.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { dossierBackLabel } from '@/config/workflowModules'
import OperatingStatementPanel from './OperatingStatementPanel'
import { CatalogueFieldsPanel, RETURNS_FIELDS, WORKFLOW_FIELDS } from '@/components/deal/dossier/CatalogueFieldsPanel'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ClipboardCheck,
  Crosshair,
  Database,
  FileText,
  Loader2,
  Lock,
  MapPin,
  Plus,
  Printer,
  Search,
  Unlock,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'

import {
  buildPatch,
  dealFileService,
  dealVersion,
  derivationOf,
  editableValue,
  groupFields,
  patchErrors,
  patchNeedsChangeReason,
  readFieldValue,
  shownFieldValue,
  holdsSponsorFigure,
  sponsorFigure,
  type DealFieldDef,
  type SponsorFigure,
} from '@/services/dealFile'
import DealPackageDialog from '@/components/deal/DealPackageDialog'
import { TermSheetEntryButton } from '@/components/deal/TermSheetEntryButton'
import { ConfirmationViewDialog } from '@/components/intake/ConfirmationView'
import { documentsService } from '@/services/documents'
import { loansService } from '@/services/loans'
import { mapsConfigService } from '@/services/mapsConfig'
import { AddressLink } from '@/components/address/AddressLink'
import { mapsUrl } from '@/components/address/mapsUrl'
import { AddressAutocomplete, type ResolvedAddress } from '@/components/address/AddressAutocomplete'
import { dateInputValue } from '@/utils/dates'
import { propertyMediaService } from '@/services/propertyMedia'
import { dossierContextService } from '@/services/dossierContext'
import { lookupBrowserPropertyMedia } from '@/services/browserPlacesMedia'
import { derivedFormulasOf, isDerivedDateField } from '@/components/deal/dossier/derivedDates'
import { dossierLoadMessage, httpStatus, loanSaveMessage, mapsConfigLoadMessage } from '@/components/deal/dossier/httpStatus'
import { ingestionService, type SourceFileRef, type SourceAnchor } from '@/services/ingestion'
import SourceViewerDialog from '@/components/intake/SourceViewerDialog'
import { LOAN_QUERY_KEYS, invalidateLoanQueries } from '@/services/loanQueryKeys'
import { useDossierTab } from '@/components/deal/dossier/useDossierTab'
import {
  DOSSIER_QUERY_KEYS,
  DOSSIER_STALE_MS,
  readDealFileValues,
} from '@/components/deal/dossier/prefetchDossier'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { useSessionPermissions } from '@/auth/useCanEditModule'
import { useDemoDataSettings } from '@/context/DemoDataContext'
import {
  useDealDossierStore,
  type DealDossierTab,
  type DealDossierTabInput,
} from '@/store/dealDossierStore'
import {
  FIELD_STATE_TOKENS,
  FieldStateBadge,
  type FieldState,
} from '@/components/fields/fieldState'
import { ValueProvenance } from '@/components/fields/ValueProvenance'
import {
  catalogueState,
  heldAsAbsent,
  heldValue,
  intakeLineageOf,
  lineageSummary,
  sponsorAttributionHolds,
  stateOfLineage,
} from '@/components/fields/lineage'
import { loanToPropertyProfile } from '@/components/deal/dossierProfiles'
import type { PropertyPhoto } from '@/types/propertyProfile'
import type { LoanDataConflict, LoanPendingRevision, Trigger } from '@/types'
import { toCamelCase } from '@/utils/transform'
import DealIntakeFlags from '@/components/deal/dossier/DealIntakeFlags'
import { dealEmails } from '@/components/deal/dossier/dealEmails'
import { PropertyPage } from '@/components/deal/dossier/PropertyPage'
import { PortfolioPropertiesPanel } from '@/components/deal/dossier/PortfolioPropertiesPanel'
import { AuditPage } from '@/components/deal/dossier/AuditPage'
import { DossierWorkspace, buildWorkspaceModel } from '@/components/deal/dossier/workspace'
import { UnderwritingPage } from '@/components/deal/dossier/UnderwritingPage'
import { ProcessPage } from '@/components/deal/dossier/SectionPages'
import { JournalPage } from '@/components/deal/dossier/JournalPage'
import { PartiesPage } from '@/components/deal/dossier/PartiesPage'
import { ScreeningChecks, ScreeningNotRun, ScreeningRuleset } from '@/components/deal/dossier/ScreeningSummary'
import { EconomicsPage } from '@/components/deal/dossier/EconomicsPage'
import { CapitalStackPage } from '@/components/deal/dossier/CapitalStackPage'
import { dealDocuments } from '@/components/deal/dossier/documentsModel'
import { getDealAttribution, getDealCorrespondence } from '@/services/dossierContext'
import { DocumentsPage } from '@/components/deal/dossier/DocumentsPage'
import { FieldMapPage } from '@/components/deal/dossier/FieldMapPage'
import { dossierLinkTarget, dossierNavFor, tabInScope } from '@/components/deal/dossier/tabs'
import { lineageSource } from '@/components/deal/dossier/valueStateChip'
import {
  applySnakePatchToLoan,
  writePipelineLoan,
  dossierSourceBanner,
  findPipelineLoan,
  loanToDealFile,
  mergeDealFile,
} from '@/components/deal/dossier/fromLoan'
import {
  collectCatalogueValues,
  deriveCatalogue,
  previewEdits,
  type DerivationOutcome,
} from '@/components/deal/dossier/deriveCatalogue'
import { overlayIngestionDossier } from '@/components/deal/dossier/ingestionOverlay'
import { DossierEditProvider, useDossierEdit } from '@/components/deal/dossier/editContext'
import {
  ADDRESS_EDIT_TARGETS,
  CUSTOM_WRITABLE,
  LOAN_UPDATE_COLUMNS,
  SYSTEM_FIELD_REASON,
  WORKSPACE_TO_SNAKE,
  isWritableField,
} from '@/components/deal/dossier/editableFields'
import { EditableValue } from '@/components/deal/dossier/EditableValue'
import { DossierHeaderFacts } from '@/components/deal/dossier/HeaderFacts'
import { BrokerStatusChip } from '@/components/deal/dossier/BrokerStatusChip'
import { DossierMetricBar, DossierMetricsProvider } from '@/components/deal/dossier/MetricBar'
import { DossierSaveControls } from '@/components/deal/dossier/DossierSaveControls'
import { VarianceNotice } from '@/components/deal/dossier/VarianceNotice'
import { dossierSaveState } from '@/components/deal/dossier/dossierSaveState'
import { resolveHeaderFigures } from '@/components/deal/dossier/headerFigures'
import { calculatedFrom } from '@/components/deal/dossier/leverageModel'
import type { EditableLoanPatch } from '@/pages/pipeline-intelligence/types'
import type { DealFilePatch } from '@/services/dealFile'
import { orderPropertyPhotos } from './dossier/propertyPhotoOrder'
import {
  catalogueValue,
  displayValue,
  formatFieldValue,
  formatVarianceFigure,
  readWebsiteUrl,
  resolveDealAddress,
} from '@/components/deal/dossier/format'
import {
  STATE_ONLY_NOTE,
  isStateOnly,
  resolveAssetPoint,
  type AssetPoint,
} from '@/components/deal/dossier/assetCoordinate'
import { NO_DEAL_EDIT, useCanEditDeal } from '@/components/deal/dossier/dealEditPermission'
import type { ResolvedAddress as SettledAddress } from '@/components/deal/dossier/addressPrecedence'

/** Deal fields that state the address, as the server names them in `dealSources`. */
const ADDRESS_PARTS = ['street', 'city', 'state', 'zip'] as const

/**
 * Is this deal field an address part (or the composed location) that Google
 * Places wrote from the building's name? One click on "Confirm" would make a
 * person the author of that guess and promote it to the verified address, so
 * the control is withheld: confirming it means typing or choosing the street.
 */
export function placesWrittenAddressField(
  name: string,
  block: { dealSources?: Partial<Record<(typeof ADDRESS_PARTS)[number], string>>; locationSource?: string } | null,
): boolean {
  if (!block) return false
  if (name === 'location') return block.locationSource === 'places'
  return (ADDRESS_PARTS as readonly string[]).includes(name)
    && block.dealSources?.[name as (typeof ADDRESS_PARTS)[number]] === 'places'
}

/**
 * Whose statement the settled address is, for the Overview's source chip. A
 * name lookup's value never survives the resolution, so `places` is not one.
 */
function sourceOfSettled(shown: SettledAddress | null): 'person' | 'package' | 'master' | null {
  const source = shown?.source
  return source === 'person' || source === 'package' || source === 'master' ? source : null
}

/** Until the server has said where each address part came from, there is no address to show. */
const ADDRESS_PENDING: AssetPoint = {
  status: 'none',
  lat: null,
  lng: null,
  source: null,
  reason: 'Waiting for where each part of the address came from.',
}

/** …and when it cannot say, nothing vouches for the deal file's address. */
const ADDRESS_UNCHECKED: AssetPoint = {
  status: 'unverified',
  lat: null,
  lng: null,
  source: null,
  reason: 'Where each part of the address came from could not be loaded.',
}

const IMAGE_NAME = /\.(jpe?g|png|gif|webp|bmp|avif)$/i
// Only what a browser can paint. `image/tiff` and `image/heic` attachments
// were fetched, handed to <img>, and shown as a broken frame with no reason.
const RENDERABLE_IMAGE_TYPE = /^image\/(jpeg|png|gif|webp|bmp|avif|svg\+xml)$/i


/** A draft holding a value the record cannot store; the message says which. */
class InvalidPatchError extends Error {}

/** The pending changes hand-type a catalogue figure, and the reason box is
 *  empty (owner decision #8, nd3-05). The Save buttons are already disabled
 *  for this; the mutation refuses it too rather than trust that alone. */
class MissingChangeReasonError extends Error {}

function buildDossierPatch(
  defs: DealFieldDef[],
  raw: ReturnType<typeof mergeDealFile>,
  draft: Record<string, unknown>,
): DealFilePatch {
  const patch = buildPatch(defs, raw, draft)
  const custom: Record<string, unknown> = { ...(patch.custom_fields ?? {}) }
  for (const [name, value] of Object.entries(draft)) {
    if (defs.some((d) => d.name === name)) continue
    if (!isWritableField(name, defs)) continue
    const next = typeof value === 'string' && value.trim() === '' ? null : value
    if (LOAN_UPDATE_COLUMNS.has(name)) patch[name] = next
    else if (CUSTOM_WRITABLE.has(name)) custom[name] = next
  }
  if (Object.keys(custom).length > 0) patch.custom_fields = custom
  return patch
}

function retryUnlessFatal(count: number, err: unknown): boolean {
  const status = httpStatus(err)
  return status !== 401 && status !== 403 && status !== 404 && count < 2
}

type Props = {
  loanId: string
  fallbackTitle?: string
  onClose: () => void
  tab?: DealDossierTabInput
  onTabChange?: (tab: DealDossierTab) => void
  /** The header's back link, named for the screen the dossier closes to. */
  backLabel?: string
}

export function DealDossier({
  loanId,
  fallbackTitle,
  onClose,
  tab: tabProp,
  onTabChange,
  backLabel: backLabelProp,
}: Props) {
  // Named for the screen under the overlay — the Pipeline, the Deal Locator's
  // drawer or Intake all open this same dossier (nd-033).
  const { pathname } = useLocation()
  const backLabel = backLabelProp ?? dossierBackLabel(pathname)
  const queryClient = useQueryClient()
  const { localUser } = useLocalAuth()
  const { isModuleDemoEnabled } = useDemoDataSettings()
  const pipelineDemo = isModuleDemoEnabled('pipeline')
  const isSuperAdmin = Boolean(localUser?.is_super_admin)
  // Whether this account may change the deal at all: pipeline:edit, the grant
  // PUT /loans/{id} (the one Save), the notes and the field approvals require.
  // A role without it (a client's viewer) was offered "Unlock to edit", typed,
  // pressed Save and only then met the 403. The API stays the authority; this
  // keeps a view-only account from being invited to edit at all.
  const canEditDeal = useCanEditDeal()
  // The header's "confirm address" opens the street editor, which saves to the
  // deal through the same Save.
  const mayConfirmAddress = canEditDeal
  const loanSnapshot = useDealDossierStore((s) => (s.loanId === loanId ? s.loanSnapshot : null))
  // `unlocked` is what every editor on the dossier follows (the edit context,
  // the header, notes, field confirmations). It is the switch AND the grant, so
  // no path that sets the switch — the address confirm, a stale store — can
  // open an input for a role that cannot save it.
  const unlockSwitch = useDealDossierStore((s) => s.unlocked)
  const unlocked = unlockSwitch && canEditDeal
  const setUnlocked = useDealDossierStore((s) => s.setUnlocked)

  const [draft, setDraft] = useState<Record<string, unknown>>({})
  const [derivation, setDerivation] = useState<DerivationOutcome | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Owner decision #8 (nd3-05): the one-line reason for a hand-typed
  // catalogue figure. Shown/required exactly when the pending changes touch
  // one (see `reasonRequired` below); cleared on discard and on a save that
  // succeeds, same as `draft`.
  const [changeReason, setChangeReason] = useState('')
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const statedFieldsRef = useRef(new Set<string>())
  const effectiveRawRef = useRef<ReturnType<typeof mergeDealFile>>(undefined)
  const fieldsRef = useRef<DealFieldDef[]>([])
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [newField, setNewField] = useState({
    name: '',
    label: '',
    group: 'Custom',
    kind: 'text' as DealFieldDef['kind'],
    options: '',
  })
  const [addFieldError, setAddFieldError] = useState<string | null>(null)
  const [packageOpen, setPackageOpen] = useState(false)
  // The header's "confirm address" box: the street editor, opened in place.
  const [confirmingAddress, setConfirmingAddress] = useState(false)
  // The Confirmation view: every field, populated and missing, and who confirmed it.
  const [confirmOpen, setConfirmOpen] = useState(false)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const draftRef = useRef<Record<string, unknown>>({})
  const changeReasonRef = useRef('')
  const [requestedTab, changeTab] = useDossierTab(tabProp, onTabChange)
  // A tab outside this account's module scope (the Step-1 analyst's
  // Underwriting) is neither listed nor rendered: a link or in-page jump to it
  // opens the Overview instead (C24). Scoped by the session's own grants, so an
  // Entra session (no local profile) is held to /auth/me as a local one is to
  // its cached profile; until that answers, a module tab is not shown.
  const sessionPermissions = useSessionPermissions()
  const tab = tabInScope(requestedTab, sessionPermissions)
  const dossierNav = useMemo(() => dossierNavFor(sessionPermissions), [sessionPermissions])
  draftRef.current = draft
  changeReasonRef.current = changeReason

  const catalogue = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
    queryFn: () => dealFileService.getCatalogue(),
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessFatal,
  })

  // The bundled demo book answers only for an account the platform lets see demo data.
  const pipelineLoan = useMemo(
    () => findPipelineLoan(queryClient, loanId, loanSnapshot, pipelineDemo),
    [queryClient, loanId, loanSnapshot, pipelineDemo],
  )
  const fromLoan = useMemo(
    () => (pipelineLoan ? loanToDealFile(pipelineLoan) : undefined),
    [pipelineLoan],
  )

  const values = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFile(loanId),
    // Missing deal file is expected for pipeline-book / demo rows. The
    // pipeline Loan already binds the template — a 404 is "no overlay".
    queryFn: () => readDealFileValues(loanId),
    // Illustrative pipeline book is not on the server; do not 404 every open.
    enabled: Boolean(loanId) && !pipelineDemo,
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })
  const savedRaw = useMemo(
    () => mergeDealFile(fromLoan, values.data ?? undefined),
    [fromLoan, values.data],
  )
  // The version this screen's values were read at (CMP-04). Sent as If-Match,
  // so a save made after someone else's is refused with 409 rather than
  // silently overwriting their change.
  const versionRef = useRef<string | null>(null)
  useEffect(() => {
    versionRef.current = dealVersion(values.data as Record<string, unknown> | undefined)
  }, [values.data])

  // The intake record this deal was created from. The pipeline row already
  // names it, so the intake reads start with the mount rather than one
  // round-trip later, once `/loans/{id}` had repeated what the row said.
  const sourceIngestionId = (
    pipelineLoan?.sourceIngestionId
    ?? savedRaw?.source_ingestion_id
    ?? savedRaw?.sourceIngestionId
  ) as string | undefined | null

  // Read from the deal's own payload (`/loans/{id}` carries both lists), or
  // from the pipeline row it was opened from while that read is in flight.
  const intakeFlags = useMemo(() => {
    const rawData = (values.data ?? null) as Record<string, unknown> | null
    const pendingRevisions = toCamelCase<LoanPendingRevision[]>(
      rawData?.pending_revisions ?? pipelineLoan?.pendingRevisions ?? [],
    )
    const dataConflicts = toCamelCase<LoanDataConflict[]>(
      rawData?.data_conflicts ?? pipelineLoan?.dataConflicts ?? [],
    )
    return {
      pendingRevisions: Array.isArray(pendingRevisions) ? pendingRevisions : [],
      dataConflicts: Array.isArray(dataConflicts) ? dataConflicts : [],
    }
  }, [values.data, pipelineLoan])

  const ingestionDossier = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.ingestionDossier(sourceIngestionId),
    queryFn: () => ingestionService.getDossier(sourceIngestionId!),
    enabled: Boolean(sourceIngestionId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  // The email this deal arrived on. Communications need a real sender,
  // recipients and subject; without the envelope the panel can only show who
  // was logged in, which is not a record of a conversation.
  const sourceRecord = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.ingestionRecord(sourceIngestionId),
    queryFn: () => ingestionService.getById(sourceIngestionId!),
    enabled: Boolean(sourceIngestionId),
    staleTime: DOSSIER_STALE_MS,
    retry: false,
  })

  // The email the deal arrived on and every later one on its thread — the
  // reply that revised the ask is the one the desk most needs (OVR-01).
  const sourceEmails = useMemo(
    () => dealEmails(sourceRecord.data, ingestionDossier.data?.sources, sourceIngestionId),
    [sourceRecord.data, ingestionDossier.data, sourceIngestionId],
  )

  const fields = useMemo(() => catalogue.data?.fields ?? [], [catalogue.data])
  const catalogueFieldNames = useMemo(
    () => (fields.length ? new Set(fields.map((def) => def.name)) : undefined),
    [fields],
  )
  const catalogueFieldKinds = useMemo(
    () => new Map(fields.map((def) => [def.name, def.kind] as [string, string])),
    [fields],
  )
  fieldsRef.current = fields

  const raw = useMemo(
    () => overlayIngestionDossier(savedRaw, ingestionDossier.data, fields),
    [savedRaw, ingestionDossier.data, fields],
  )
  effectiveRawRef.current = raw

  // A deal the server holds previews its edits through the server's own
  // derivation (POST /derivation/preview): one rule set, one scale. The
  // browser's copy drifted from the engine (no stabilized-value rule,
  // ECON-29) and read the LTV column's stored fraction as a percent, so the
  // save bar printed a $335K "implied" loan beside a $33.5M one (LPR-10).
  // The local mirror remains only for the illustrative pipeline book, which
  // has no server record to ask about.
  const serverBackedRef = useRef(false)
  serverBackedRef.current = !pipelineDemo && Boolean(values.data)
  const previewSeqRef = useRef(0)
  const previewTimerRef = useRef<number | undefined>(undefined)
  const recomputeDerivation = useCallback(
    (nextDraft: Record<string, unknown>) => {
      const base = effectiveRawRef.current
      const defs = fieldsRef.current
      if (!base || defs.length === 0) return
      if (!serverBackedRef.current) {
        const values = collectCatalogueValues(base, defs, nextDraft)
        setDerivation(deriveCatalogue(values, statedFieldsRef.current))
        return
      }
      window.clearTimeout(previewTimerRef.current)
      const seq = ++previewSeqRef.current
      // The edits as the save would write them (one normaliser, one scale).
      const edits = previewEdits(defs, base, nextDraft)
      // Nothing numeric edited: the saved record, and the save's own result, stand.
      if (edits === null) return
      // Every numeric edit is back at its saved value: nothing left to preview.
      if (Object.keys(edits).length === 0) {
        setDerivation(null)
        return
      }
      previewTimerRef.current = window.setTimeout(() => {
        loansService
          .previewDerivation(loanId, edits)
          .then((result) => {
            if (seq !== previewSeqRef.current) return
            setDerivation({ derived: result.derived, notes: result.notes, conflicts: result.variances })
          })
          .catch(() => {
            if (seq === previewSeqRef.current) setDerivation(null)
          })
      }, 250)
    },
    [loanId],
  )
  useEffect(() => () => window.clearTimeout(previewTimerRef.current), [])

  useEffect(() => {
    recomputeDerivation(draftRef.current)
  }, [raw, fields, recomputeDerivation])

  const lineage = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId),
    queryFn: () => loansService.getFieldLineage(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  const notes = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.notes(loanId),
    queryFn: () => loansService.listNotes(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  const activity = useQuery({
    queryKey: LOAN_QUERY_KEYS.activity(loanId),
    queryFn: () => loansService.getActivity(loanId),
    enabled: Boolean(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  // The chain the Audit tab's Trail lists; the AUDIT badge counts it (DJA-12).
  const stageAudit = useQuery({
    queryKey: LOAN_QUERY_KEYS.stageAudit(loanId),
    queryFn: () => loansService.getStageAudit(loanId),
    enabled: Boolean(loanId),
    retry: retryUnlessFatal,
  })

  const mapsConfig = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.mapsConfig,
    queryFn: () => mapsConfigService.get(),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  const timeline = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.timeline(loanId),
    queryFn: () => loansService.getStageTimeline(loanId),
    enabled: Boolean(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  const [viewerAnchor, setViewerAnchor] = useState<SourceAnchor | null>(null)
  const [sourceUnavailable, setSourceUnavailable] = useState<string | null>(null)
  const [viewerSha, setViewerSha] = useState<string | null>(null)
  // When set, the source viewer opens to point at where a MISSING value lives
  // rather than to verify one that is already recorded.
  const [designateField, setDesignateField] = useState<string | null>(null)
  const [auditSegment, setAuditSegment] = useState<number | null>(null)
  const [activityLoanStage, setActivityLoanStage] = useState<string | null>(null)
  const [photos, setPhotos] = useState<PropertyPhoto[]>([])
  // One read of the intake record serves the envelope, the source files and
  // the screen results. It was fetched twice under two keys — the largest
  // payload on the page, shipped over the wire two times per open.
  const sourceFiles: SourceFileRef[] = sourceRecord.data?.rawFilePaths ?? []
  const documents = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.documents(loanId),
    queryFn: () => documentsService.listForLoan(loanId),
    enabled: Boolean(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
    // Carried up from CapitalStackPage, which used to poll this same key
    // privately. One fetch, one list, one merge.
    refetchInterval: 30_000,
  })

  useEffect(() => {
    setDraft({})
    setChangeReason('')
    setSaveError(null)
    setUnlocked(false)
    // The header's street box belongs to the deal it was opened on; the next
    // deal's editor opens only when its own "confirm address" is pressed.
    setConfirmingAddress(false)
    statedFieldsRef.current = new Set()
    setDerivation(null)
    setAuditSegment(null)
    setActivityLoanStage(null)
  }, [loanId, setUnlocked])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (useDealDossierStore.getState().unlocked && Object.keys(draftRef.current).length > 0) {
        setSaveError('Save or discard your changes before closing.')
        return
      }
      onClose()
    }
    window.addEventListener('keydown', handler)
    closeRef.current?.focus()
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const sourceFileKey = sourceFiles.map((f) => f.sha256).join(',')
  const documentKey = (documents.data ?? []).map((d) => d.id).join(',')
  useEffect(() => {
    let cancelled = false
    const urls: string[] = []
    async function loadPhotos() {
      // Every image at once. They were awaited one after another, so the
      // hero image — the most visible "it has loaded" signal — arrived last,
      // after every other attachment had been pulled in full.
      const fetches: Promise<PropertyPhoto | null>[] = []
      for (const doc of documents.data ?? []) {
        const image = RENDERABLE_IMAGE_TYPE.test(doc.contentType ?? '') || IMAGE_NAME.test(doc.filename)
        if (!image) continue
        fetches.push(
          documentsService
            .download(doc.id)
            .then((blob) => ({ src: URL.createObjectURL(blob), alt: doc.filename, source: 'Deal file' as const }))
            .catch(() => null),
        )
      }
      if (sourceIngestionId) {
        for (const file of sourceFiles) {
          if (!IMAGE_NAME.test(file.filename)) continue
          fetches.push(
            ingestionService
              .getSourceBlob(sourceIngestionId, file.sha256)
              .then((blob) => ({ src: URL.createObjectURL(blob), alt: file.filename, source: 'Deal file' as const }))
              .catch(() => null),
          )
        }
      }
      const settled = await Promise.all(fetches)
      const next: PropertyPhoto[] = []
      for (const photo of settled) {
        if (!photo) continue
        urls.push(photo.src)
        next.push(photo)
      }
      if (!cancelled) setPhotos(next)
    }
    void loadPhotos()
    return () => {
      cancelled = true
      for (const url of urls) URL.revokeObjectURL(url)
    }
  }, [documentKey, sourceFileKey, sourceIngestionId])

  const groups = useMemo(() => groupFields(fields), [fields])
  const rulesByTarget = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const rule of catalogue.data?.rules ?? []) {
      map.set(rule.target, [...(map.get(rule.target) ?? []), rule.note])
    }
    return map
  }, [catalogue.data])

  const lineageByField = useMemo(() => {
    const map = new Map<string, LoanFieldLineageField>()
    for (const field of lineage.data?.fields ?? []) map.set(field.fieldName, field)
    return map
  }, [lineage.data])

  /** What the intake record says of a value the deal itself does not hold
   *  yet — the dossier overlays those values, so they are labelled with the
   *  record's own state rather than as an unsourced extraction. */
  const intakeLineage = useMemo(
    () => intakeLineageOf(ingestionDossier.data),
    [ingestionDossier.data],
  )

  const patch = useMemo(
    () => buildDossierPatch(fields, savedRaw, draft),
    [fields, savedRaw, draft],
  )
  const dirtyCount =
    Object.keys(patch).filter((k) => k !== 'custom_fields').length +
    Object.keys(patch.custom_fields ?? {}).length
  // Entries the record cannot hold: Save waits for them (nd-062).
  const invalidCount = useMemo(() => patchErrors(fields, patch).length, [fields, patch])
  // Owner decision #8: a hand-typed catalogue figure (an amount, a rate, a
  // ratio) needs a one-line reason before Save runs.
  const reasonRequired = useMemo(() => patchNeedsChangeReason(fields, patch), [fields, patch])
  const reasonProvided = changeReason.trim().length > 0

  const effectiveValue = useCallback(
    (name: string) => {
      if (name in draft) return draft[name]
      if (derivation?.derived[name] !== undefined) return derivation.derived[name]
      return catalogueValue(raw, fields, name)
    },
    [draft, derivation, raw, fields],
  )

  const isDerivedField = useCallback(
    (name: string) => derivation?.derived[name] !== undefined && !(name in draft),
    [derivation, draft],
  )

  // The stored values the engine derived (lineage state Derived): outputs, so
  // an unlocked dossier shows them, never an input over them (R1, nd-030).
  // A derived date (Maturity, Extension Maturity) is the exception: typing it
  // states it, so it is offered as an input (`derivedFormulasOf`).
  const derivedFormulas = useMemo(
    () => derivedFormulasOf(lineage.data?.fields ?? []),
    [lineage.data?.fields],
  )
  const derivedFormula = useCallback(
    (name: string) => (name in draft ? null : derivedFormulas.get(name) ?? null),
    [derivedFormulas, draft],
  )
  // Each saved figure's agreed state, for the chip every slot shows (C05): the
  // Property tab's geocoder MSA read as fact. A value a person is typing over
  // is theirs until saved, so it carries no saved state.
  const valueSource = useCallback(
    (name: string) => (name in draft ? null : lineageSource(name, lineageByField.get(name))),
    [lineageByField, draft],
  )
  // What each derived value is computed from, so the unlocked dossier can
  // offer its inputs wherever the output is shown (R1: a person corrects the
  // NOI and the value, never the cap rate they produce). The inputs the
  // lineage recorded for THIS deal first — the rule that actually fired —
  // else every rule the catalogue has for the target (dscr_io has one per
  // funding basis), which is what the server names in its 422 too.
  const ruleInputs = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const rule of catalogue.data?.rules ?? []) {
      map.set(rule.target, [...new Set([...(map.get(rule.target) ?? []), ...(rule.inputs ?? [])])])
    }
    return map
  }, [catalogue.data])
  const inputsOf = useCallback(
    (name: string) => {
      const recorded = (lineageByField.get(name)?.derivation?.inputs ?? [])
        .map((input) => input.field)
        .filter(Boolean)
      return recorded.length > 0 ? [...new Set(recorded)] : ruleInputs.get(name) ?? []
    },
    [lineageByField, ruleInputs],
  )

  const addField = useMutation({
    mutationFn: () =>
      dealFileService.addCatalogueField({
        name: newField.name.trim(),
        label: newField.label.trim(),
        group: newField.group.trim() || 'Custom',
        kind: newField.kind,
        options:
          newField.kind === 'select'
            ? newField.options
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : undefined,
      }),
    onSuccess: async (result) => {
      setAddFieldError(null)
      setAddFieldOpen(false)
      setNewField({ name: '', label: '', group: 'Custom', kind: 'text', options: '' })
      queryClient.setQueryData(LOAN_QUERY_KEYS.dealFieldCatalogue, {
        fields: result.fields,
        rules: catalogue.data?.rules ?? [],
      })
      await queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue })
      setDraft((prev) => ({ ...prev, [result.field.name]: '' }))
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      setAddFieldError(typeof detail === 'string' ? detail : 'Could not add field.')
    },
  })

  const setField = useCallback(
    (name: string, value: unknown) => {
      statedFieldsRef.current.add(name)
      setDraft((prev) => {
        const next = { ...prev, [name]: value }
        draftRef.current = next
        recomputeDerivation(next)
        return next
      })
      setSavedAt(null)
    },
    [recomputeDerivation],
  )

  // The catalogue, where its subject lives. Built here because the draft,
  // lineage and save machinery are this component's.
  /**
   * Fill the address fields around Street from one picked suggestion.
   *
   * Google returns the whole address broken into parts, so someone who has
   * told us the street has already told us the city, the state and the postal
   * code. The same street again only completes what is blank. A DIFFERENT
   * address is a correction, and its city, state and ZIP go with its street:
   * leaving the old ones under a new street is how a deal came to hold two
   * addresses at once. `location` is not written here — the server recomposes
   * it from the parts, so one rule composes it for every editor.
   */
  const applyResolvedAddress = useCallback((address: ResolvedAddress) => {
    const read = (name: string, source: 'saved' | 'draft') => {
      const def = fieldsRef.current.find((d) => d.name === name)
      const value = def
        ? source === 'draft' && name in draft ? draft[name] : readFieldValue(raw, def)
        : undefined
      return typeof value === 'string' ? value.trim() : value == null ? '' : String(value)
    }
    const savedStreet = read('street', 'saved').toLowerCase()
    const differentAddress = Boolean(address.street) && savedStreet !== address.street.trim().toLowerCase()
    const take = (name: string, value: string) => {
      if (value && (differentAddress || !read(name, 'draft'))) setField(name, value)
    }
    if (address.street) setField('street', address.street)
    take('city', address.city)
    take('state', address.state)
    take('zip', address.postalCode)
    take('country', address.country)
  }, [draft, raw, setField])

  const catalogueRowProps = useCallback((def: DealFieldDef): FieldRowProps => ({
    def,
    // The column's "nothing stated" marker (loan_amount 0.0) is no value —
    // whether the column's own rule (`shownFieldValue`) or the lineage's held
    // value (`heldAsAbsent`) says so.
    saved: derivation?.derived[def.name] ?? (
      heldAsAbsent(lineageByField.get(def.name), shownFieldValue(raw, def))
        ? null
        : shownFieldValue(raw, def)),
    draftValue: draft[def.name],
    hasDraft: def.name in draft,
    lineage: lineageByField.get(def.name),
    intake: intakeLineage.get(def.name),
    derivedNote: derivation?.notes?.[def.name] ?? rulesByTarget.get(def.name)?.[0],
    justDerived: Boolean(derivation && def.name in derivation.derived && !(def.name in draft)),
    conflict: derivation?.conflicts?.[def.name],
    onChange: (v: unknown) => setField(def.name, v),
    onResolvedAddress: applyResolvedAddress,
    mapsKey: mapsConfig.data?.apiKey ?? null,
    sponsor: sponsorFigure(raw, def.name),
  }), [raw, draft, lineageByField, intakeLineage, derivation, rulesByTarget, setField, applyResolvedAddress, mapsConfig.data?.apiKey])

  /** Open the document a value was stated in, at the place it was stated.
   *
   *  The lineage now names the file's content hash, the page, the cell and
   *  the line, so the viewer opens that file with that place highlighted.
   *  It used to fall back to the first stored file with no highlight when the
   *  name drifted — which opened the broker's email for a value the Parties
   *  tab set, and an unmarked PDF for the LTV (DJA-07). A value with no
   *  stored document behind it says so instead of opening a substitute. */
  const openFieldSource = useCallback((fieldName: string) => {
    if (!sourceFiles.length) return
    const entry = lineageByField.get(fieldName)
    const prov = entry?.provenance ?? null
    const doc = (prov?.document ?? '').toLowerCase()
    const isMessage = doc.startsWith('email body') || doc.startsWith('email subject')
      || (prov?.method === 'message_received')
    const hit =
      (prov?.sourceSha256 ? sourceFiles.find((file) => file.sha256 === prov.sourceSha256) : undefined)
      ?? (doc ? sourceFiles.find((file) => (file.filename ?? '').toLowerCase() === doc) : undefined)
      ?? (isMessage ? sourceFiles.find((file) => file.kind === 'email_source') : undefined)
    setDesignateField(null)
    if (!hit) {
      setViewerAnchor(null)
      setSourceUnavailable(
        prov?.document
          ? `“${prov.document}” is not a stored file on this deal, so there is nothing to open. No substitute document has been selected.`
          : 'This value has no stored document behind it. No substitute document has been selected.',
      )
      setViewerSha('unavailable')
      return
    }
    const page = prov?.page ? Number(prov.page) : undefined
    const line = prov?.lineNo != null && prov.lineNo !== '' ? Number(prov.lineNo) : undefined
    setViewerAnchor({
      sha256: hit.sha256,
      page: Number.isFinite(page) ? page : undefined,
      cell: prov?.cellRef ?? undefined,
      lineNo: Number.isFinite(line) ? line : undefined,
      snippet: prov?.raw ?? undefined,
      kind: doc.startsWith('email subject') ? 'email_subject' : isMessage ? 'email_body' : undefined,
    })
    setSourceUnavailable(null)
    setViewerSha(hit.sha256)
  }, [sourceFiles, lineageByField])

  // A person confirming a value — any catalogue field (LPR-07). Recorded as
  // theirs, with the reason, and the lineage then shows Confirmed.
  const confirmField = useMutation({
    mutationFn: (fieldName: string) =>
      loansService.approveField(loanId, fieldName, 'Confirmed on the deal dossier'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.fieldLineage(loanId) })
    },
  })

  const findFieldInSource = useCallback((fieldLabel: string) => {
    if (!sourceFiles.length) return
    setDesignateField(fieldLabel)
    setViewerSha(sourceFiles[0].sha256)
  }, [sourceFiles])

  const catalogueGroupNames = CATALOGUE_GROUPS_BY_TAB[tab] ?? NO_CATALOGUE_GROUPS
  const showsCatalogue = catalogueGroupNames.length > 0


  const title =
    (raw?.property_name as string | undefined) ?? fallbackTitle ?? pipelineLoan?.property ?? 'Deal dossier'

  // The calculation service's figures — the same cached read Economics and
  // the Capital Stack make — so a header figure the deal does not hold but
  // the engine computes reads the same, marked Derived, on every tab.
  const calculations = useQuery({
    queryKey: LOAN_QUERY_KEYS.calculations(loanId),
    queryFn: () => loansService.getCalculations(loanId),
    enabled: Boolean(loanId) && !pipelineDemo,
    retry: false,
  })

  /** The display value of each header slot on the metric bar (one definition
   *  on every tab — see HEADER_GROUPS), and which of them are derived. */
  const header = useMemo(
    () =>
      resolveHeaderFigures({
        get: (name) => catalogueValue(raw, fields, name),
        calculated: calculatedFrom(calculations.data?.derived),
        capitalStack: raw?.capital_stack,
        product: raw?.product,
      }),
    [raw, fields, calculations.data],
  )
  const headerDisplays = header.displays

  const controlId = (raw?.control_id ?? raw?.controlId) as string | undefined
  const displayId = controlId || loanId

  const loading = !raw && (catalogue.isLoading || values.isLoading)
  const loadError = raw ? undefined : (values.error ?? catalogue.error)
  const denied = !raw && httpStatus(loadError) === 403
  const sourceBanner = dossierSourceBanner(values.error, Boolean(raw))

  const workspaceSave = useMutation({
    mutationFn: async (incoming?: EditableLoanPatch) => {
      const merged = { ...draftRef.current }
      if (incoming) {
        for (const [key, value] of Object.entries(incoming)) {
          if (value === undefined) continue
          merged[WORKSPACE_TO_SNAKE[key] ?? key] = value
        }
        setDraft(merged)
        draftRef.current = merged
      }
      const patch = buildDossierPatch(fields, savedRaw, draftRef.current)
      // A value the record cannot hold (an LTV above 100%) is refused here,
      // with the reason, rather than saved and re-derived from.
      const invalid = patchErrors(fields, patch)
      if (invalid.length > 0) throw new InvalidPatchError(invalid.join(' '))
      // Owner decision #8: a hand-typed catalogue figure needs a one-line
      // reason. The Save buttons are already disabled for this; refused here
      // too rather than trusting that alone.
      const reason = changeReasonRef.current.trim()
      if (patchNeedsChangeReason(fields, patch) && !reason) {
        throw new MissingChangeReasonError('Add a one-line reason for the hand-typed figure.')
      }
      try {
        return await dealFileService.save(loanId, patch, versionRef.current, reason || undefined)
      } catch (err) {
        if (httpStatus(err) === 404 && pipelineLoan) {
          const updated = applySnakePatchToLoan(pipelineLoan, {
            ...patch,
            ...(patch.custom_fields ?? {}),
          })
          writePipelineLoan(queryClient, updated)
          useDealDossierStore.setState({ loanSnapshot: updated })
          return { ...loanToDealFile(updated), _sessionOnly: true }
        }
        throw err
      }
    },
    onSuccess: async (result) => {
      versionRef.current = dealVersion(result as Record<string, unknown>) ?? versionRef.current
      setDerivation(derivationOf(result) as DerivationOutcome)
      setDraft({})
      draftRef.current = {}
      setChangeReason('')
      changeReasonRef.current = ''
      setSavedAt(Date.now())
      if ((result as { _sessionOnly?: boolean })._sessionOnly) {
        setSaveError(
          'Held on this pipeline record in this session. No deal file on the server — nothing was written remotely.',
        )
      } else {
        setSaveError(null)
        await invalidateLoanQueries(queryClient, loanId)
      }
    },
    onError: (err: unknown) => {
      // The server says why (admin-only stage gate, a field it refused, a
      // newer save by someone else); a generic line hid all three. A patch the
      // browser itself refused to send names the fields it refused.
      setSaveError(
        err instanceof InvalidPatchError || err instanceof MissingChangeReasonError
          ? `${err.message} Nothing was written.`
          : loanSaveMessage(err),
      )
      // Someone saved first: load their values now. The unsaved draft stays,
      // so the person sees both and chooses whether to save theirs over it.
      if (httpStatus(err) === 409) void values.refetch()
    },
  })

  const conflictEntries = Object.entries(derivation?.conflicts ?? {})

  const screen = sourceRecord.data?.screenResults

  // The asset, the sponsor, that sponsor's book and the comparable set. A
  // database read, so it resolves with the record rather than after it.
  const dossierContext = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.context(loanId),
    queryFn: () => dossierContextService.getContext(loanId),
    enabled: Boolean(loanId) && !pipelineDemo,
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })
  const propertyMaster = dossierContext.data?.property ?? null
  const contextSettled = pipelineDemo || dossierContext.isFetched || dossierContext.isError
  // The server's account of where each address part came from arrives with the
  // dossier context. The deal file alone cannot say: a street Google Places
  // wrote from the building's name reads exactly like one a document stated.
  // So until the context has answered the address is undecided (nothing is
  // printed, nothing billed is asked), and if it cannot answer, the deal file's
  // address stays unverified instead of being trusted as package-stated.
  const contextFailed = !pipelineDemo && dossierContext.isError && !dossierContext.data
  const addressDecided = contextSettled && !contextFailed

  // The address by the one order every surface uses — person-entered, then
  // package-stated, then the property master — so a street a person corrected
  // is the street the header, the Property tab, the map and the printed report
  // all show. Nothing here asks Google for an address: opening a deal never
  // makes a billed lookup, and a street is only ever one somebody stated.
  //
  // The street, city, state and ZIP come from the deal file; where each came
  // from comes from the dossier context. Those are two requests, and a save
  // refetches both: whichever lands first would pair its new half with the
  // other's old one (a Places guess read as the person's street, or the
  // person's street read as a guess). So while either is in flight the last
  // address that both agreed on stays on screen, and the new one replaces it
  // when both have landed.
  //
  // "Agreed on" means a snapshot built from a context AND a deal file that had
  // both answered for this deal. Anything earlier is never held: the deal file
  // is often on hand first (the pipeline row is cached), and read without the
  // context beside it a Places-written street looks package-stated, so a
  // snapshot taken then would outlive the context that withholds it. Before the
  // context has landed there is no address to render at all (the deal file
  // alone is never read as one); after it lands, the current context renders.
  const liveAddress = useMemo(
    () => (pipelineDemo || dossierContext.data ? resolveDealAddress(raw, fields, dossierContext.data) : null),
    [pipelineDemo, raw, fields, dossierContext.data],
  )
  const addressRefreshing = values.isFetching || dossierContext.isFetching
  const settledRef = useRef<{ loanId: string; shown: SettledAddress; agreed: boolean } | null>(null)
  if (
    !addressRefreshing
    || !settledRef.current
    || settledRef.current.loanId !== loanId
    || !settledRef.current.agreed
  ) {
    settledRef.current = liveAddress
      ? {
          loanId,
          shown: liveAddress,
          agreed: !addressRefreshing && Boolean(dossierContext.data) && values.isFetched,
        }
      : null
  }
  const shownAddress = settledRef.current?.shown ?? null
  const address = addressDecided && shownAddress ? shownAddress.full : ''
  const addressBlock = dossierContext.data?.address ?? null
  // What the master's coordinate alone says about the place. It seeds the site
  // lookup and nothing else; the point every surface draws is decided below.
  const seedPoint = resolveAssetPoint({
    addressBlock,
    resolved: shownAddress,
    master: propertyMaster,
  })
  // Lookups keyed on a building (photos, Street View, the parcel and the rings
  // around it) run only for a street a person or a document stands behind. For
  // a city, an unsupported address or nothing at all they would be fetched for
  // the wrong place: a Washington Monument pin, a parcel outline of the nearest
  // building.
  //
  // A street also needs a city and state, or a ZIP, beside it (the server's
  // `site_intel_store._lookup_target` rule): "100 Main Street" exists in every
  // state, so geocoding the bare string answers for some other town's.
  const streetPlaceable =
    Boolean(shownAddress?.street)
    && (Boolean(shownAddress?.city && shownAddress?.state) || Boolean(shownAddress?.zip))
  const lookupsAllowed = addressDecided && seedPoint.status === 'asset' && streetPlaceable
  // The area facts (county, demographics) are true of a city too, so a deal
  // that states only "Charleston, SC" still gets them; the server withholds
  // everything about a building from that answer.
  const siteIntelAllowed =
    addressDecided && (lookupsAllowed || seedPoint.status === 'city')

  // County, flood zone, demographics, transit, schools and the property's own
  // website. Fans out to Google and three US government services, so it loads
  // beside the record, never gating it. Two things keep it to ONE fan-out per
  // open: the answer the server already holds for this property arrives
  // inline with the dossier context, and when it does not, the lookup waits
  // for the context so it runs once with the stored coordinate — the
  // coordinate used to sit in the query key, and its arrival re-ran the
  // whole fan-out a second time.
  const keptIntel = dossierContext.data?.siteIntel ?? null
  // Only for the address the dossier would ask about: a corrected address is
  // a different place, and the kept answer for the old one must not stand in.
  const storedIntel =
    siteIntelAllowed
    && keptIntel
    && !keptIntel.unavailable
    && keptIntel.addressUsed.trim().toLowerCase() === address.trim().toLowerCase()
      ? keptIntel
      : null
  const siteIntel = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.siteIntel(loanId, address),
    queryFn: () =>
      dossierContextService.getSiteIntel({
        address,
        // Only a stored point that belongs to this street; never the master's
        // point for some other address, and none at all for a city, whose
        // answer is the area's and not a building's.
        lat: lookupsAllowed && seedPoint.source === 'master' ? seedPoint.lat : undefined,
        lng: lookupsAllowed && seedPoint.source === 'master' ? seedPoint.lng : undefined,
        loanId,
      }),
    enabled: siteIntelAllowed && !storedIntel && Boolean(address.trim()),
    placeholderData: storedIntel ?? undefined,
    staleTime: 30 * 60 * 1000,
    retry: false,
  })
  // What the site lookup says about THIS address, or nothing: an answer the
  // server declined to give, or one fetched before the address stopped being a
  // supported one, is not shown beside a different place.
  const intelData = siteIntelAllowed && !siteIntel.data?.unavailable ? siteIntel.data ?? null : null

  // Every file that ever arrived on this deal, from every message in the
  // conversation. `sourceIngestionId` names only the record the deal was
  // created from, so reading it alone hid every reply and forward after the
  // first — which on a live deal is most of the correspondence.
  const correspondence = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.correspondence(loanId),
    queryFn: () => getDealCorrespondence(loanId),
    enabled: Boolean(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  // Whose figure each value is. A dossier populated from an intake package is
  // populated almost entirely from documents the sponsor wrote, and shown
  // without an author the sponsor's numbers read as the deal's numbers.
  const attribution = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.attribution(loanId),
    queryFn: () => getDealAttribution(loanId),
    enabled: Boolean(loanId),
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  // The Journal's Sentiment Tracker and Recent Insights. Read on the server
  // from the deal's own record (a lexicon count over what was written, and
  // facts from structured records that each name their source) — nothing is
  // scored or inferred in the browser. Fetched only when the Journal is open.
  const journalIntelligence = useQuery({
    queryKey: ['journal-intelligence', loanId],
    queryFn: () => dossierContextService.getJournalIntelligence(loanId),
    enabled: Boolean(loanId) && !pipelineDemo && tab === 'journal',
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  // One answer to "what documents does this deal have", shared by every panel
  // that asks. Each was passed the vault alone, so a deal whose files all
  // arrived by email read as having none.
  const dealDocumentList = useMemo(
    () =>
      dealDocuments(
        documents.data ?? [],
        correspondence.data?.files.length ? { files: correspondence.data.files } : undefined,
      ),
    [documents.data, correspondence.data],
  )

  const profileContext = useMemo(
    () => ({
      property: propertyMaster,
      address: addressBlock,
      addressDecided,
      settledAddress: shownAddress,
      sponsor: dossierContext.data?.sponsor ?? null,
      sponsorPortfolio: dossierContext.data?.sponsorPortfolio ?? null,
      comparables: dossierContext.data?.comparables ?? null,
      intel: intelData,
      broker: dossierContext.data?.broker ?? null,
    }),
    [dossierContext.data, propertyMaster, addressBlock, addressDecided, shownAddress, intelData],
  )
  const profile = raw ? loanToPropertyProfile(raw, profileContext) : null

  // The site's own URL: what the deal states, else what Google lists for the
  // place. Media is only ever pulled from a URL one of those two named, and a
  // place that is not a building has no website of its own to offer.
  const recordedWebsite = raw ? readWebsiteUrl(raw, fields) : ''
  const website = recordedWebsite || (lookupsAllowed ? intelData?.website : '') || ''

  // Waits for the site intel to settle so it runs once, with the website
  // Google resolved — the website used to sit in the query key, and its
  // arrival re-ran the whole media fan-out. Street-level addresses only: a
  // photo search on a city or on an unsupported address finds a different
  // building and shows it as this one.
  const remoteMedia = useQuery({
    queryKey: DOSSIER_QUERY_KEYS.media(loanId, address),
    queryFn: () => propertyMediaService.lookup({ address, website: website || null }),
    enabled:
      lookupsAllowed && Boolean(loanId) && Boolean(address.trim() || website) && !siteIntel.isFetching,
    staleTime: DOSSIER_STALE_MS,
    retry: retryUnlessFatal,
  })

  const browserMedia = useQuery({
    queryKey: ['browser-property-media', loanId, address, mapsConfig.data?.apiKey],
    queryFn: () => lookupBrowserPropertyMedia(mapsConfig.data!.apiKey!, address),
    enabled: lookupsAllowed && Boolean(loanId && address.trim() && mapsConfig.data?.apiKey),
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessFatal,
  })
  // A cached answer for an address that no longer qualifies is not shown.
  const remoteMediaData = lookupsAllowed ? remoteMedia.data : undefined
  const browserMediaData = lookupsAllowed ? browserMedia.data : undefined

  /**
   * Where this property actually is, and how far that can be trusted.
   *
   * The master record is a stored geocode; the media lookups return the
   * coordinates Google resolved for the address TEXT while fetching imagery.
   * Which of them may be used is decided in one place (`assetCoordinate.ts`):
   * only a street a person or a document stands behind gets a building
   * coordinate, a city gets a city-level one, and an address nothing supports
   * gets none. The header's address link, the Property page's map and the
   * comps map's subject pin read the same value, so they cannot disagree.
   */
  const assetPoint: AssetPoint = !contextSettled
    ? ADDRESS_PENDING
    : contextFailed
      ? ADDRESS_UNCHECKED
      : resolveAssetPoint({
          addressBlock,
          resolved: shownAddress,
          master: propertyMaster,
          browser: browserMediaData,
          server: remoteMediaData,
          intel: intelData,
        })
  const mapLat = assetPoint.status === 'asset' ? assetPoint.lat : null
  const mapLng = assetPoint.status === 'asset' ? assetPoint.lng : null
  // Nothing a document or a person supports, or nothing at all. The header says
  // so rather than printing whatever text the deal happens to hold — and says
  // nothing yet while it cannot know.
  const addressNotStated =
    addressDecided && (assetPoint.status === 'unverified' || (Boolean(raw) && !address.trim()))
  // A city or state a package stated, with no street to go with it (including a
  // street that was set aside): the header shows the place and says what is missing.
  const stateOnly = addressDecided && isStateOnly(shownAddress)
  // A city and state, or a state alone, with no street to go with it.
  const streetNotStated =
    addressDecided && (assetPoint.status === 'city' || stateOnly) && !shownAddress?.street
  // The address is still being settled: nothing may call it "not on record" yet.
  const addressPending = !contextSettled
  // The one rule for every surface that opens the address as its street, city,
  // state and postal code once unlocked: the header, the Overview's Address
  // row and the Property summary. Only a settled street a document or a person
  // stands behind is opened. Otherwise the deal file's street and city may be
  // a Places name lookup's (Washington Square at the Monument, city "Iconic"):
  // the Property tab and the Overview opened them pre-filled as the deal's
  // street, where the header already refused to. The header's "confirm
  // address" editor, which never offers the guess, states a street instead.
  const addressEditable =
    !loading
    && Boolean(raw)
    && contextSettled
    && !contextFailed
    && Boolean(address.trim())
    && !addressNotStated
    && !streetNotStated
  // The address as the person is correcting it: the settled parts with their
  // unsaved edits over them, composed the way the settled address is. The
  // header and the Overview printed the saved address while the popover held
  // the edit, so a corrected street looked as though it had not been taken.
  const draftedAddress = useMemo(() => {
    const parts = ['street', 'city', 'state', 'zip'] as const
    if (!addressEditable || !parts.some((part) => part in draft)) return address
    const part = (name: (typeof parts)[number]) =>
      name in draft ? String(draft[name] ?? '').trim() : String(shownAddress?.[name] ?? '').trim()
    const tail = [part('state'), part('zip')].filter(Boolean).join(' ')
    return [part('street'), part('city'), tail].filter(Boolean).join(', ') || address
  }, [addressEditable, draft, address, shownAddress])

  const galleryPhotos = useMemo((): PropertyPhoto[] => {
    const server = (remoteMediaData?.photos ?? []).map((p) => ({
      src: propertyMediaService.photoUrl(p.token),
      alt: p.alt,
      source: p.source,
    }))
    // Both lookups can offer a Street View shot of the same coordinates —
    // the server's is proxied through a token, the browser's is a direct
    // Static API URL that needs that API enabled on the browser key and this
    // site on its referrer list, which it often is not. When the server has
    // one, it is the same picture and the reliable copy: keep only that.
    const serverHasStreet = server.some((p) => p.source === 'Street View')
    const browser = (browserMediaData?.photos ?? []).filter(
      (p) => !(serverHasStreet && p.source === 'Street View'),
    )
    const seen = new Set<string>()
    const merged: PropertyPhoto[] = []
    for (const p of [...photos, ...browser, ...server]) {
      if (seen.has(p.src)) continue
      seen.add(p.src)
      merged.push(p)
    }
    // Rank before returning. This list is not just a gallery: merged[0] is the
    // dossier's hero image and the deal's thumbnail everywhere it is listed,
    // and concatenation order put email attachments first — which is how a
    // sponsor's letterhead and a resident's snapshot came to stand in for the
    // building on live deals.
    return orderPropertyPhotos(merged)
  }, [photos, remoteMediaData, browserMediaData])


  // Conditions come from the dossier context: `Trigger` is a lazy relationship
  // on the loan, so it is absent from every /loans payload, and the Conditions
  // tab and the Overview's conditions / obligations / risk panels read from it.
  // The in-memory book row is the fallback for demo and offline rows.
  const triggers: Trigger[] = useMemo(() => {
    const fromContext = dossierContext.data?.conditions
    if (fromContext?.length) {
      return fromContext.map((condition) => ({
        id: condition.id,
        type: condition.type,
        due: condition.due,
        status: (condition.status || 'upcoming') as Trigger['status'],
        severity: (condition.severity || 'medium') as Trigger['severity'],
        description: condition.description ?? undefined,
      }))
    }
    return pipelineLoan?.triggers ?? []
  }, [dossierContext.data, pipelineLoan])

  const workspaceModel = useMemo(() => {
    if (!raw) return null
    return buildWorkspaceModel({
      raw,
      defs: fields,
      loan: pipelineLoan,
      lineage: lineage.data?.fields ?? [],
      attribution: attribution.data ?? null,
      segments: timeline.data?.segments ?? [],
      documents: documents.data ?? [],
      sourceFiles: correspondence.data?.files ?? [],
      activity: activity.data ?? [],
      stageAudit: stageAudit.data ?? null,
      notes: notes.data ?? [],
      sourceEmails,
      triggers,
      derivationNotes: derivation?.notes ?? {},
      effectiveValue,
      // The Overview's Address row reads the same settled address as the
      // header, not the deal file's raw street, city and location.
      address: addressNotStated ? '' : draftedAddress,
      addressSource: addressNotStated ? null : sourceOfSettled(shownAddress),
      addressPending,
      addressEditable,
      drafted: (name) => (name in draft ? { value: draft[name] } : null),
    })
  }, [
    addressNotStated,
    addressPending,
    addressEditable,
    draft,
    shownAddress,
    draftedAddress,
    raw,
    fields,
    pipelineLoan,
    lineage.data?.fields,
    attribution.data,
    timeline.data?.segments,
    documents.data,
    correspondence.data,
    activity.data,
    stageAudit.data,
    notes.data,
    sourceEmails,
    triggers,
    derivation?.notes,
    effectiveValue,
  ])

  const addNote = useMutation({
    mutationFn: (body: string) => loansService.addNote(loanId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['loan-notes', loanId] })
      await queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.activity(loanId) })
      await queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.stageAudit(loanId) })
    },
  })

  // Address components + market prose the intake engine parsed from the source
  // (register A3/A4/A6). These live on the linked ingestion's extracted_fields,
  // not on the loan row — nested keys are camelCased by toCamelCase. Surfaced
  // read-only in the Record → Property tab so the operator can confirm what the
  // parser read against the source without it being silently written to the loan.
  const parsedIntake = useMemo(() => {
    const ef = (sourceRecord.data?.extractedFields ?? {}) as Record<string, unknown>
    const text = (key: string): string | null => {
      const v = ef[key]
      return typeof v === 'string' && v.trim() ? v.trim() : null
    }
    return {
      street: text('propertyStreet'),
      city: text('propertyCity'),
      state: text('propertyState'),
      postal: text('propertyPostal'),
      marketNarrative: text('marketNarrative'),
      sponsorNarrative: text('sponsorNarrative'),
    }
  }, [sourceRecord.data?.extractedFields])
  const hasParsedAddress = Boolean(
    parsedIntake.street || parsedIntake.city || parsedIntake.state || parsedIntake.postal,
  )
  // The parsed address shows beside the Audit catalogue whenever the record
  // has one. It was gated on `currentGroup === 'Property'`, and the current
  // group is 'Deal Identity' unless a super-admin adds a field — so the block,
  // and the market narrative inside it, never rendered (UI-01). The narrative
  // now reads on the Property tab (PropertyPage → Location intelligence).
  const showParsedProperty = hasParsedAddress

  // Two things lived on the Record tab besides the fields, and neither had
  // anywhere else to be: the platform-wide field-add form, and what the intake
  // package parsed about the address. They move with the catalogue rather than
  // going down with the tab.
  // The catalogue lives on Audit alone now, so its header extras follow it
  // there: without this the parsed-intake block and the super-admin add-field
  // control still keyed off Property and Overview, which no longer render a
  // single catalogue row between them.
  const catalogueHeader = useMemo(() => {
    if (tab !== 'audit') return null
    const parsed = showParsedProperty ? (

          <div className="mb-3 rounded-lg border border-sky-500/20 bg-sky-500/[0.04] px-3 py-2">
            <div className="flex items-center gap-1.5">
              <MapPin size={11} className="text-sky-300" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-sky-300">
                Parsed from intake
              </span>
            </div>
            {hasParsedAddress && (
              <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1">
                {([
                  ['Street', parsedIntake.street],
                  ['City', parsedIntake.city],
                  ['State', parsedIntake.state],
                  ['Postal', parsedIntake.postal],
                ] as const).map(([label, value]) => (
                  <div key={label} className="flex items-center gap-2 text-[11px]">
                    <span className="w-[52px] shrink-0 text-stone-500">{label}</span>
                    <span className="text-stone-200">{value ?? '—'}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-2 text-[9px] text-stone-600">
              Read from the linked intake record. Enter or confirm the values on the deal below to write them.
            </p>
          </div>
    ) : null
    if (!isSuperAdmin) return parsed
    return (
      <div className="space-y-2">
        {parsed}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setAddFieldOpen((o) => !o)}
            className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-amber-300 hover:bg-amber-400/10"
          >
            <Plus size={11} /> Add catalog field
          </button>
        </div>
        {addFieldOpen && isSuperAdmin && (
          <div className="mb-3 rounded-lg border border-amber-400/20 bg-black/40 px-3 py-2 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
              Add catalog field (platform-wide)
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <input
                placeholder="snake_case_name"
                value={newField.name}
                onChange={(e) => setNewField((p) => ({ ...p, name: e.target.value }))}
                className="rounded border border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[11px] text-stone-200"
              />
              <input
                placeholder="Label"
                value={newField.label}
                onChange={(e) => setNewField((p) => ({ ...p, label: e.target.value }))}
                className="rounded border border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[11px] text-stone-200"
              />
              <input
                placeholder="Group"
                value={newField.group}
                onChange={(e) => setNewField((p) => ({ ...p, group: e.target.value }))}
                className="rounded border border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[11px] text-stone-200"
              />
              <select
                value={newField.kind}
                onChange={(e) =>
                  setNewField((p) => ({
                    ...p,
                    kind: e.target.value as DealFieldDef['kind'],
                  }))
                }
                className="rounded border border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[11px] text-stone-200"
              >
                {(['text', 'currency', 'percent', 'number', 'date', 'select'] as const).map(
                  (k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ),
                )}
              </select>
            </div>
            {newField.kind === 'select' && (
              <input
                placeholder="Options, comma-separated"
                value={newField.options}
                onChange={(e) => setNewField((p) => ({ ...p, options: e.target.value }))}
                className="w-full rounded border border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[11px] text-stone-200"
              />
            )}
            {addFieldError && <p className="text-[10px] text-red-300">{addFieldError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={addField.isPending || !newField.name.trim() || !newField.label.trim()}
                onClick={() => addField.mutate()}
                className="rounded bg-amber-400/90 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-40"
              >
                {addField.isPending ? 'Saving…' : 'Create field'}
              </button>
              <button
                type="button"
                onClick={() => setAddFieldOpen(false)}
                className="rounded border border-stone-700 px-2.5 py-1 text-[10px] text-stone-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }, [tab, isSuperAdmin, addFieldOpen, newField, addFieldError, addField, parsedIntake,
      showParsedProperty, hasParsedAddress])

  const catalogueNode = useMemo(() => {
    if (!showsCatalogue) return null
    const byGroup = new Map(groups.map((g) => [g.group, g]))
    const pages = CATALOGUE_PAGES.map((page) => ({
      label: page.label,
      groups: page.groups.map((g) => byGroup.get(g)).filter(Boolean) as typeof groups,
    })).filter((page) => page.groups.length > 0)
    if (pages.length === 0) return null
    // Anything the lineage tracks that the catalogue does not define. The
    // retired Field lineage page showed these; folding it into the catalogue
    // would otherwise drop them silently, which is the one way a merge of two
    // lists can lose a record.
    const known = new Set(fields.map((def) => def.name))
    const offCatalogue = (lineage.data?.fields ?? []).filter((row) => !known.has(row.fieldName))
    // One state per figure, from the lineage's agreed state. There is no
    // "recorded" fallback any more: that fallback is what printed "extracted ·
    // Not recorded" on computed, defaulted and hand-typed values alike.
    const rowState = (props: FieldRowProps, value: unknown): FieldState => {
      if (props.justDerived) return 'derived'
      const empty = value === null || value === undefined || value === ''
      return catalogueState(props.lineage, empty, props.intake)
    }
    // The catalogue and what is tracked beside it, counted apart: the list read
    // '133 records' beside the 127-field catalogue with nothing saying six
    // deal-record columns sit outside it (LPR-26).
    const countNote = offCatalogue.length
      ? `${fields.length} catalog fields · ${offCatalogue.length} outside the catalog`
      : undefined
    return (
      <AuditTable actions={catalogueHeader} countNote={countNote} catalogue={catalogue.data} items={[
        ...pages.flatMap(page => page.groups.flatMap(group => group.fields.map(def => {
          const props = catalogueRowProps(def)
          const value = props.hasDraft ? props.draftValue : props.saved
          const state = rowState(props, value)
          const label = FIELD_STATE_TOKENS[state].label
          const variance = props.lineage?.variance
          return { id: def.name, fieldKey: def.name, label: def.label, value: formatFieldValue(value, def.kind, def.name) || '—', section: page.label, state: variance ? `${label} · variance` : label, source: catalogueSource(props, state), details: (close: () => void) => <>
            <div className="text-[11px] text-stone-400">{def.help ?? 'Recorded deal field'}</div>
            {props.justDerived && props.derivedNote && <div className="rounded border border-violet-300/20 p-3 text-xs"><span className="text-violet-200">Recalculated from your edits</span><p className="mt-2 font-mono">{props.derivedNote}</p></div>}
            {props.conflict && <div className="rounded border border-amber-400/25 p-3 text-xs text-amber-100">Variance, for information: stated {formatVarianceFigure(props.conflict.stated, def.kind)}; the other figures give {formatVarianceFigure(props.conflict.computed ?? props.conflict.implied, def.kind)}. {props.conflict.note}</div>}
            {props.lineage || props.intake ? (
              <ValueProvenance
                field={props.lineage ?? props.intake}
                kind={def.kind}
                state={state}
                onOpenSource={sourceFiles.length ? () => { close(); openFieldSource(def.name) } : undefined}
                onConfirm={unlocked && state !== 'derived' && !(value === null || value === undefined || value === '') && !props.hasDraft && !placesWrittenAddressField(def.name, addressBlock) ? () => confirmField.mutate(def.name) : undefined}
                confirming={confirmField.isPending && confirmField.variables === def.name}
              />
            ) : null}
            <FieldRow {...props} expandedDetails onOpenSource={sourceFiles.length ? () => { close(); openFieldSource(def.name) } : undefined} onFindInSource={sourceFiles.length ? label => { close(); findFieldInSource(label) } : undefined} />
          </> }
        }))),
        ...offCatalogue.map(field => {
          const held = heldValue(field)
          const state = catalogueState(field, held === null || held === undefined || held === '')
          return { id: field.fieldName, label: field.fieldName, value: displayValue(held), section: 'Outside the catalog', state: FIELD_STATE_TOKENS[state].label, source: lineageSummary(field) || '—', details: () => <div className="space-y-3 text-xs text-stone-300"><ValueProvenance field={field} state={state} /><pre className="whitespace-pre-wrap">{JSON.stringify(field.history ?? [], null, 2)}</pre></div> }
        }),
      ]} />
    )
  }, [showsCatalogue, groups, fields, catalogue.data, lineage.data?.fields, catalogueRowProps, catalogueHeader,
      sourceFiles.length, openFieldSource, findFieldInSource, unlocked, confirmField, addressBlock])

  const editValue = useMemo(
    () => ({
      unlocked,
      draft,
      raw,
      defs: fields,
      setField,
      derivation,
      effectiveValue,
      isDerivedField,
      derivedFormula,
      valueSource,
      inputsOf,
    }),
    [unlocked, draft, raw, fields, setField, derivation, effectiveValue, isDerivedField, derivedFormula, valueSource, inputsOf],
  )

  return (
    <DossierEditProvider value={editValue}>
     <DossierMetricsProvider>
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={() => {
        if (unlocked && dirtyCount > 0) {
          setSaveError('Save or discard your changes before closing.')
          return
        }
        onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Deal dossier — ${title}`}
        data-dossier-tab={tab}
        className="dossier-shell relative flex h-[96vh] w-[min(1920px,98vw)] max-w-[98vw] flex-col overflow-hidden rounded-xl border border-amber-400/15 bg-[#090909] shadow-2xl [&_thead_th]:text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid flex-shrink-0 gap-3 border-b border-stone-800/80 px-3 py-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex shrink-0 items-center gap-1 pt-0.5 text-[10px] text-stone-500 hover:text-stone-300 sm:pt-1"
            >
              <ChevronLeft size={14} /> {backLabel}
            </button>
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {galleryPhotos[0] ? (
                <img
                  src={galleryPhotos[0].src}
                  alt={galleryPhotos[0].alt || `${title} property`}
                  className="h-16 w-24 shrink-0 rounded-md border border-amber-300/15 object-cover shadow-lg sm:h-[72px] sm:w-28"
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="min-w-0 break-words text-[21px] font-semibold leading-tight tracking-[-0.02em] text-stone-100 sm:text-[24px]">
                    {/* The deal's name is its Property / Deal Name field:
                        unlocked, it is corrected where it is read. */}
                    <EditableValue name="property_name" label="Property / Deal Name" display={title} />
                  </h2>
                  {/* The Control ID is identity, never an input (it is what
                      every later email is matched on). */}
                  <span
                    className="rounded border border-stone-800 bg-black/30 px-1.5 py-0.5 font-mono text-[9px] text-stone-500"
                    title={unlocked ? SYSTEM_FIELD_REASON : undefined}
                  >
                    {displayId}
                  </span>
                  {!loading && workspaceModel?.creditState && workspaceModel.creditState !== '—' ? (
                    <span className="rounded-full border border-emerald-400/20 bg-emerald-400/[0.08] px-2 py-0.5 text-[9px] font-medium text-emerald-300">
                      {workspaceModel.creditState}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 flex min-w-0 items-start gap-1.5 text-[11px] leading-relaxed text-stone-400">
                  {unlocked && addressEditable ? (
                    // Unlocked, the address text opens its fields, so it can no
                    // longer be the map link it is locked; the pin beside it,
                    // already on the line at the same size, becomes that link,
                    // and nothing on the line moves.
                    <a
                      href={mapsUrl({ address, lat: mapLat, lng: mapLng }) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in Google Maps"
                      aria-label="Open the address in Google Maps"
                      data-testid="address-map-link"
                      className="mt-0.5 inline-flex shrink-0 text-amber-300/65 hover:text-amber-200"
                    >
                      <MapPin size={12} aria-hidden="true" />
                    </a>
                  ) : (
                    <MapPin size={12} className="mt-0.5 shrink-0 text-amber-300/65" />
                  )}
                  {/* Unlocked, a settled street address opens the fields it is
                      stated in. An address the package does not state keeps
                      its "confirm address" flow, which offers suggestions. */}
                  {unlocked && addressEditable ? (
                    <EditableValue
                      name="street"
                      label="Property address"
                      fields={ADDRESS_EDIT_TARGETS}
                      popover
                      display={draftedAddress}
                    />
                  ) : (
                  <DossierAddressLine
                    loading={loading || !raw || !contextSettled}
                    address={address}
                    notStated={addressNotStated}
                    streetNotStated={streetNotStated}
                    stateOnly={stateOnly}
                    canConfirm={mayConfirmAddress}
                    unchecked={contextFailed}
                    onRetry={() => void dossierContext.refetch()}
                    lat={mapLat}
                    lng={mapLng}
                    onConfirm={() => {
                      // The street box is the existing address editor; the draft
                      // it fills is saved by the Property tab's save controls.
                      setUnlocked(true)
                      changeTab('property')
                      setConfirmingAddress(true)
                    }}
                  />
                  )}
                </div>
                {mayConfirmAddress && confirmingAddress && unlocked && (addressNotStated || streetNotStated) ? (
                  <div
                    data-testid="address-confirm-editor"
                    className="mt-1.5 flex min-w-0 max-w-[520px] items-center gap-2"
                  >
                    <AddressAutocomplete
                      id="deal-header-street"
                      // What a person has typed, else the street the settled address
                      // shows — empty when the only street on record was withheld, so
                      // the box never offers a name-lookup guess as the thing to confirm.
                      value={displayValue('street' in draft ? draft.street : shownAddress?.street ?? '')}
                      onChange={(value) => setField('street', value)}
                      onResolved={applyResolvedAddress}
                      apiKey={mapsConfig.data?.apiKey ?? null}
                      placeholder="Type the street address the package states"
                      className="dossier-edit-hairline border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[12px] focus:border-amber-400/50"
                    />
                    <button
                      type="button"
                      onClick={() => setConfirmingAddress(false)}
                      className="shrink-0 text-[10px] text-stone-500 hover:text-stone-300"
                    >
                      Done
                    </button>
                  </div>
                ) : null}
                {!loading && raw && workspaceModel ? (
                  <DossierHeaderFacts
                    meta={workspaceModel.headerMeta}
                    relationshipOwner={workspaceModel.relationshipOwner}
                  />
                ) : null}
                {/* The CRM's verdict on the deal's broker, on every tab (UI-05). */}
                {!loading && raw && dossierContext.data?.broker ? (
                  <div className="mt-1.5">
                    <BrokerStatusChip
                      name={dossierContext.data.broker.name}
                      status={{
                        blocklisted: Boolean(dossierContext.data.broker.isBlocklisted),
                        preferred: Boolean(dossierContext.data.broker.isPreferred),
                        qualityScore: dossierContext.data.broker.qualityScore ?? null,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex min-h-7 min-w-0 flex-wrap items-center justify-start gap-2 lg:max-w-[680px] lg:justify-end">
            {/* Document upload lives on the Documents tab — not a second
                attach control in this header. */}
            {dirtyCount > 0 && (
              <span className="text-[11px] text-amber-300">
                {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}
              </span>
            )}
            {savedAt && dirtyCount === 0 && (
              <span className="flex items-center gap-1 text-[11px] text-emerald-400">
                <Check size={11} /> Saved
              </span>
            )}
            {/* Laid out locked too (invisible and inert), so unlocking never
                widens this column and reflows the header beside it. */}
            <DossierSaveControls
              unlocked={unlocked}
              dirtyCount={dirtyCount}
              invalidCount={invalidCount}
              reasonRequired={reasonRequired}
              reasonProvided={reasonProvided}
              reason={changeReason}
              onReasonChange={setChangeReason}
              pending={workspaceSave.isPending}
              onDiscard={() => {
                setDraft({})
                draftRef.current = {}
                setChangeReason('')
                changeReasonRef.current = ''
                previewSeqRef.current += 1
                setDerivation(null)
                setSaveError(null)
              }}
              onSave={() => workspaceSave.mutate(undefined)}
            />
            {/* Term sheet, print and lock stay anchored beside Close; edit
                controls appear to their left, so unlocking never shifts them. */}
            <TermSheetEntryButton
              loanId={loanId}
              onLeave={() => {
                if (unlocked && dirtyCount > 0) {
                  setSaveError('Save or discard your changes before opening the term sheet.')
                  return false
                }
                onClose()
                return true
              }}
            />
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              aria-label="Review and confirm"
              title="Review & confirm — every field, populated and missing, and who has confirmed it"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-amber-300 transition-colors hover:bg-amber-300/10 hover:text-amber-200 focus-visible:outline focus-visible:outline-amber-300"
            >
              <ClipboardCheck size={17} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setPackageOpen(true)}
              aria-label="Print dossier"
              title="Print dossier — choose sections, stages and photos"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-amber-300 transition-colors hover:bg-amber-300/10 hover:text-amber-200 focus-visible:outline focus-visible:outline-amber-300"
            >
              <Printer size={17} aria-hidden="true" />
            </button>
            {/* A role that cannot edit the deal still sees the lock, held shut
                and saying why, as Reporting draws its own role-gated controls:
                the header keeps one layout for every role, and the reader is
                told rather than left to find the refusal after a Save. */}
            <button
              type="button"
              role="switch"
              aria-checked={unlocked}
              aria-label={unlocked ? 'Lock editing' : 'Unlock to edit'}
              disabled={!canEditDeal}
              title={
                !canEditDeal
                  ? NO_DEAL_EDIT
                  : unlocked
                    ? 'Lock editing for the whole dossier'
                    : 'Unlock every field on this dossier. Locked by default so a stray keystroke cannot alter a credit file.'
              }
              onClick={() => {
                if (!canEditDeal) return
                if (unlocked && dirtyCount > 0) {
                  setSaveError('Discard or save your changes before locking.')
                  return
                }
                setSaveError(null)
                setUnlocked(!unlocked)
              }}
              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors focus-visible:outline focus-visible:outline-amber-300 ${
                !canEditDeal
                  ? 'cursor-not-allowed border-stone-800 text-stone-600'
                  : unlocked
                    ? 'border-amber-300/60 bg-amber-300 text-stone-950'
                    : 'border-stone-700 text-stone-300 hover:border-amber-300/30'
              }`}
            >
              {/* The icon stays where it is; only the lock state changes. */}
              {unlocked ? <Unlock className="h-3.5 w-3.5" aria-hidden="true" /> : <Lock className="h-3.5 w-3.5" aria-hidden="true" />}
            </button>
            <button
              ref={closeRef}
              onClick={() => {
                if (unlocked && dirtyCount > 0) {
                  setSaveError('Save or discard your changes before closing.')
                  return
                }
                onClose()
              }}
              aria-label="Close deal dossier"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-stone-500 transition-colors hover:bg-stone-800 hover:text-stone-200"
            >
              <X size={14} />
            </button>
            {!loading && raw && tab === 'parties' && (
              <DossierMetricBar header={headerDisplays} derived={header.derived} lineage={lineage.data?.fields ?? []} />
            )}
          </div>
        </div>

        {/* What intake still owes this deal — a later email's figure waiting
            on a person, open data questions — and the way to settle them
            (stress tests OVR-01, OVR-09, REG-06, CMP-09). */}
        {!loading && raw && !pipelineDemo && (
          <DealIntakeFlags
            sourceIngestionId={sourceIngestionId}
            pendingRevisions={intakeFlags.pendingRevisions}
            dataConflicts={intakeFlags.dataConflicts}
            onLeave={onClose}
          />
        )}

        {/* UI-13: with the pipeline module in demo, the deal-file, calculation,
            dossier-context, journal-intelligence and intake-flag reads above
            are all disabled on this view. Said once, here, so a walkthrough on
            the bundled book is never mistaken for the live intake path. */}
        {!loading && raw && pipelineDemo && (
          <div
            role="status"
            data-testid="dossier-demo-banner"
            className="flex flex-shrink-0 items-center gap-2 border-b border-amber-400/15 bg-amber-400/[0.05] px-3 py-1.5 text-[10px] text-amber-200/90"
          >
            <Database size={11} className="shrink-0 text-amber-300" />
            <span>Demo data: live intake wiring is disabled on this view</span>
          </div>
        )}

        {/* ONE bar of figures, not two. The page merges its own metrics onto
            these header slots by field name, so a page that also states the
            loan amount lends this slot its icon and its sponsor attribution
            rather than printing the number a second time forty pixels below.
            See MetricBar.tsx. */}
        {!loading && raw && tab !== 'overview' && tab !== 'parties' && (
          <DossierMetricBar compact header={headerDisplays} derived={header.derived} lineage={lineage.data?.fields ?? []} />
        )}


        {/* Body — Overview fits one viewport; other tabs scroll */}
        <div
          className={`min-h-0 flex-1 ${
            tab === 'parties' || tab === 'audit' || tab === 'overview' || tab === 'journal' || tab === 'economics' || tab === 'capital_stack' || tab === 'property' || tab === 'underwriting' || tab === 'documents' || tab === 'field_map'
              ? 'flex flex-col overflow-hidden px-2 py-2'
              : 'overflow-y-auto px-3 py-2'
          }`}
        >
          {loading && (
            <div className="flex h-full items-center justify-center gap-2 text-[12px] text-stone-500">
              <Loader2 size={14} className="animate-spin" /> Loading the deal dossier…
            </div>
          )}

          {!loading && denied && (
            <div className="flex h-full items-center justify-center px-8 text-center text-[12px] text-stone-400">
              You do not have permission to view this deal record. Ask an administrator for
              pipeline access.
            </div>
          )}

          {!loading && loadError != null && !denied && (
            <div className="flex h-full items-center justify-center px-8 text-center text-[12px] text-red-300">
              {dossierLoadMessage(loadError, loanId)}
            </div>
          )}

          {!loading && raw && saveError && (
            <p role="alert" className="mb-3 rounded bg-red-500/[0.08] px-3 py-2 text-[11px] text-red-300">
              {saveError}
            </p>
          )}

          {!loading && raw && sourceBanner && (
            <p className="mb-3 rounded border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-100">
              {sourceBanner}
            </p>
          )}

          {!loading && raw && tab === 'overview' && (
            <div className="min-h-0 flex-1">
              <DossierWorkspace
                model={workspaceModel ?? undefined}
                raw={raw}
                defs={fields}
                loan={pipelineLoan}
                operatingHistory={propertyMaster?.operatingHistory ?? null}
                lineage={lineage.data?.fields ?? []}
                attribution={attribution.data ?? null}
                segments={timeline.data?.segments ?? []}
                documents={documents.data ?? []}
                sourceFiles={correspondence.data?.files ?? []}
                activity={activity.data ?? []}
                notes={notes.data ?? []}
                triggers={triggers}
                derivationNotes={derivation?.notes ?? {}}
                effectiveValue={effectiveValue}
                onGoto={(next) => changeTab(next)}
                onSaveNote={(body) => addNote.mutate(body)}
                editDisabled={!unlocked || addNote.isPending}
                onOpenSource={async (field) => {
                  const record = sourceIngestionId ? await queryClient.ensureQueryData({queryKey: DOSSIER_QUERY_KEYS.ingestionRecord(sourceIngestionId), queryFn: () => ingestionService.getById(sourceIngestionId)}).catch(() => null) : null
                  const files = record?.rawFilePaths ?? []
                  const normalize = (value: string) => value.replace(/_/g, '').toLowerCase()
                  const entry = lineage.data?.fields.find(item => item.fieldName === field)
                  const parsed = record?.parseReport?.fields.find(item => normalize(item.field) === normalize(field ?? '') && (!entry?.source || item.source === entry.source))
                  const source = entry?.source ?? parsed?.source ?? ''
                  const file = files.find(item => parsed?.anchor?.sha256 === item.sha256)
                    ?? files.find(item => item.filename.toLowerCase() === source.toLowerCase())
                    ?? (/^email_(body|subject)$/.test(source) ? files.find(item => item.kind === 'email_source') : undefined)
                  setDesignateField(null)
                  setViewerAnchor(parsed?.anchor ?? (parsed ? { lineNo: parsed.lineNo, snippet: parsed.raw, cell: parsed.cellRef ?? undefined, kind: source === 'email_subject' ? 'email_subject' : source === 'email_body' ? 'email_body' : undefined } : null))
                  setSourceUnavailable(file ? null : 'The recorded source file is unavailable. No substitute document has been selected.')
                  setViewerSha(file?.sha256 ?? 'unavailable')
                }}
                onStageInspect={(stage) => {
                  setActivityLoanStage(stage.loanStage)
                  setAuditSegment(null)
                  changeTab('audit')
                }}
              />
              {/* UI-11: the desk's own notes, people and clocks were on the Audit
                  tab alone. Collapsed, so the Overview still fits its viewport. */}
              <CatalogueFieldsPanel title="Workflow" fields={WORKFLOW_FIELDS} defaultOpen={false} />
            </div>
          )}

          {!loading && raw && tab === 'property' && profile && (
            <div className="flex min-h-0 flex-1 flex-col">
              {/* A portfolio's listed properties, and the create action they
                  never had (CMP-07). Renders nothing on any other deal. */}
              <PortfolioPropertiesPanel loanId={loanId} canEdit={unlocked} />
              <div className="min-h-0 flex-1">
              <PropertyPage
                // One deal's map mode, failed Aerial and expanded panels are
                // not the next deal's: this component outlives a deal switch.
                key={loanId}
                website={website || null}
                onLinkPhotoSource={(url) => setField('website', url)}
                loanId={loanId}
                defs={fields}
                raw={raw}
                profile={profile}
                address={address}
                photos={galleryPhotos}
                documents={dealDocumentList}
                activity={activity.data ?? []}
                mapsKey={mapsConfig.data?.apiKey ?? null}
                mapsConfigured={Boolean(mapsConfig.data?.configured)}
                mapsError={mapsConfig.isError ? mapsConfigLoadMessage(mapsConfig.error) : null}
                mapsLoading={mapsConfig.isLoading}
                mapsAerialEnabled={mapsConfig.data?.aerialEnabled}
                mediaNotes={[...(browserMediaData?.notes ?? []), ...(remoteMediaData?.notes ?? [])]}
                mediaLoading={remoteMedia.isLoading || browserMedia.isLoading}
                formattedAddress={
                  browserMediaData?.formattedAddress
                  ?? remoteMediaData?.formattedAddress
                  ?? intelData?.formattedAddress
                  ?? null
                }
                mapLat={mapLat}
                mapLng={mapLng}
                mapBasis={assetPoint.status}
                addressPending={addressPending}
                addressStateOnly={stateOnly}
                addressEditable={addressEditable}
                cityLat={assetPoint.status === 'city' ? assetPoint.lat : null}
                cityLng={assetPoint.status === 'city' ? assetPoint.lng : null}
                propertyMaster={propertyMaster}
                propertyMatchedBy={dossierContext.data?.propertyMatchedBy ?? null}
                addressBlock={addressBlock}
                addressParts={
                  addressDecided && shownAddress ? { city: shownAddress.city, state: shownAddress.state } : null
                }
                addressUnchecked={contextFailed}
                siteIntel={intelData}
                siteIntelLoading={siteIntel.isLoading || !contextSettled}
                comparables={dossierContext.data?.comparables ?? null}
                packageCompTablesUnread={dossierContext.data?.packageCompTablesUnread ?? []}
                marketNarrative={pipelineDemo ? null : parsedIntake.marketNarrative}
                onGoto={(next) => changeTab(next)}
              />
              </div>
            </div>
          )}

          {!loading && raw && tab === 'economics' && (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
              <EconomicsPage
                raw={raw}
                defs={fields}
                loanId={loanId}
                operatingHistory={propertyMaster?.operatingHistory ?? null}
                activity={activity.data ?? []}
                notes={notes.data ?? []}
                onGoto={(next) => changeTab(next)}
                canGoto={(next) => tabInScope(next, sessionPermissions) === next}
              />
              {/* Register K5. The overflow bucket has always kept the lines a
                  chart of accounts could not place; until now nothing showed
                  them to anyone, which from a reviewer's chair is the same as
                  having rejected them. */}
              <OperatingStatementPanel loanId={loanId} />
              {/* UI-11: the equity-and-returns terms, EBITDA and the renovated-unit
                  count were on the Audit tab alone. */}
              <CatalogueFieldsPanel title="Returns" fields={RETURNS_FIELDS} />
            </div>
          )}

          {!loading && raw && tab === 'capital_stack' && (
            <div className="min-h-0 flex-1">
              <CapitalStackPage
                raw={raw}
                defs={fields}
                loanId={loanId}
                activity={activity.data ?? []}
                notes={notes.data ?? []}
                documents={dealDocumentList}
                onGoto={(next) => changeTab(next)}
              />
            </div>
          )}

          {!loading && raw && tab === 'parties' && (
            <PartiesPage
              loanId={loanId}
              sponsorPortfolio={dossierContext.data?.sponsorPortfolio ?? null}
              brokerLink={
                sourceIngestionId && !pipelineDemo
                  ? {
                      ingestionId: sourceIngestionId,
                      resolution: sourceRecord.data?.parseReport?.brokerResolution,
                      brokerId: pipelineLoan?.brokerId ?? sourceRecord.data?.brokerId,
                      brokerFirmId: sourceRecord.data?.brokerFirmId,
                    }
                  : null
              }
              documents={dealDocumentList}
              activity={activity.data ?? []}
              sponsorRemark={pipelineDemo ? null : parsedIntake.sponsorNarrative}
              sponsorMatchedBy={dossierContext.data?.sponsorMatchedBy ?? null}
            />
          )}

          {!loading && raw && workspaceModel && tab === 'journal' && (
            <div className="min-h-0 flex-1">
              <JournalPage
                loanId={loanId}
                model={workspaceModel}
                segments={timeline.data?.segments ?? []}
                noteDisabled={!unlocked}
                sentiment={journalIntelligence.data?.sentiment ?? null}
                insights={journalIntelligence.data?.insights ?? []}
                onOpenActivity={() => changeTab('audit')}
              />
            </div>
          )}

          {!loading && raw && tab === 'documents' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <DocumentsPage
                loanId={loanId}
                sourceIngestionId={sourceIngestionId}
                sourceFiles={sourceFiles}
                sourcePackage={sourceRecord.data}
                sourceLoading={sourceRecord.isLoading}
                catalogue={catalogue.data}
                correspondence={correspondence.data ?? null}
                onOpenSource={(sha) => setViewerSha(sha)}
                documents={documents.data ?? []}
                activity={activity.data ?? []}
                requirements={triggers}
                loading={documents.isLoading}
                error={documents.error}
                onGoto={(next) => changeTab(next)}
              />
            </div>
          )}

          {!loading && raw && workspaceModel && tab === 'underwriting' && (
            <div className="min-h-0 flex-1">
              <UnderwritingPage
                loanId={loanId}
                model={workspaceModel}
                raw={raw}
                screen={screen}
                documents={dealDocumentList}
                activity={activity.data ?? []}
                onGoto={(next) => changeTab(next)}
                workflow={
                  <ProcessPage
                    loanId={loanId}
                    auditSegment={auditSegment}
                    onSelectSegment={(i) => {
                      setAuditSegment(i)
                    }}
                    screening={
                      <ScreeningPanel
                        sourceIngestionId={sourceIngestionId}
                        screen={screen}
                        loading={Boolean(sourceIngestionId) && sourceRecord.isLoading}
                      />
                    }
                  />
                }
              />
            </div>
          )}

          {!loading && raw && tab === 'audit' && (
            <AuditPage
              catalogue={catalogueNode}
              loanId={loanId}
              lineage={lineage.data?.fields ?? []}
              catalogueFieldNames={catalogueFieldNames}
              fieldKinds={catalogueFieldKinds}
              variances={lineage.data?.variances?.length}
              notes={notes.data ?? []}
              activity={activity.data ?? []}
              loading={activity.isLoading || lineage.isLoading}
              selectedLoanStage={activityLoanStage}
              selectedSegmentIndex={auditSegment}
              onSelectSegment={(i) => {
                setAuditSegment(i)
                if (i === null) setActivityLoanStage(null)
              }}
            />
          )}

          {!loading && raw && tab === 'field_map' && (
            <div className="min-h-0 flex-1">
              <FieldMapPage loanId={loanId} demo={pipelineDemo} />
            </div>
          )}

        </div>

        <nav className="flex flex-shrink-0 items-center gap-0.5 overflow-x-auto border-t border-stone-800/80 px-2 py-1.5">
          {dossierNav.map((item) => {
            const count = item.countKey && workspaceModel ? workspaceModel.tabCounts[item.countKey] : null
            return (
            <button
              key={item.id}
              type="button"
              title={item.hint}
              onClick={() => changeTab(item.id)}
              className={`whitespace-nowrap rounded px-2.5 py-1.5 text-[10px] uppercase tracking-[0.12em] ${
                tab === item.id ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500 hover:text-stone-300'
              }`}
            >
              {item.label}
              {count != null && count > 0 ? ` (${count})` : ''}
            </button>
          )})}
        </nav>

        {/* The bottom bar carries only what the header cannot: a save error
            and the variance notice (with the reason box beside them when the
            draft needs one). Discard and Save, the change count and the
            hand-typed-figure reason are the header's, on every tab. The bar
            used to open on the first keystroke of any draft; opening, it
            pushed the tab strip up 53px and shortened every panel above it,
            so a layout that fit one screen stopped fitting the moment a
            person typed (21 Oct review, Overview and Capital Stack at
            1366x768 and 1600x950). */}
        {(saveError || conflictEntries.length > 0) && (
          <div className="flex-shrink-0 border-t border-amber-400/10 px-4 py-2.5">
            {saveError && (
              <div className="mb-2 flex items-start gap-2 rounded border border-red-500/25 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-200">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                <span>{saveError}</span>
              </div>
            )}

            {/* Rule R1/R4: a variance between a stated figure and what the
                other figures imply is shown for information. */}
            <VarianceNotice entries={conflictEntries} defs={fields} />

            {reasonRequired && (
              // Owner decision #8 (nd3-05): a hand-typed catalogue figure —
              // an amount, a rate, a ratio — needs a one-line reason before
              // this save can run. Shown only when the pending changes
              // include one; Save stays disabled until it is filled, the
              // same shape `dossierSaveState` already uses for an invalid
              // entry.
              <div className="mb-2">
                <label htmlFor="dossier-change-reason" className="mb-1 block text-[10px] text-stone-500">
                  Reason for the hand-typed figure (required)
                </label>
                <input
                  id="dossier-change-reason"
                  type="text"
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder="e.g. T-12 line 53, trailing 12 months"
                  maxLength={1000}
                  className="w-full rounded border border-amber-300/30 bg-black/40 px-2.5 py-1.5 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/60 focus:outline-none"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-[10px] text-stone-600">
                Every change is recorded against your name in the deal&rsquo;s audit trail.
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDraft({})
                    draftRef.current = {}
                    setChangeReason('')
                    changeReasonRef.current = ''
                    previewSeqRef.current += 1
                    setDerivation(null)
                    setSaveError(null)
                  }}
                  disabled={dirtyCount === 0}
                  className="rounded border border-stone-700 px-3 py-1.5 text-[11px] text-stone-300 transition-colors hover:bg-stone-800 disabled:opacity-40"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (unlocked && dirtyCount > 0) {
                      setSaveError('Save or discard your changes before closing.')
                      return
                    }
                    onClose()
                  }}
                  className="rounded border border-stone-700 px-3 py-1.5 text-[11px] text-stone-300 transition-colors hover:bg-stone-800"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => workspaceSave.mutate(undefined)}
                  disabled={
                    dossierSaveState({
                      dirtyCount, invalidCount, pending: workspaceSave.isPending, reasonRequired, reasonProvided,
                    }).disabled
                  }
                  title={
                    dossierSaveState({
                      dirtyCount, invalidCount, pending: workspaceSave.isPending, reasonRequired, reasonProvided,
                    }).title
                  }
                  className="flex items-center gap-1.5 rounded bg-amber-400/90 px-3 py-1.5 text-[11px] font-medium text-black transition-colors hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-stone-800 disabled:text-stone-500"
                >
                  {workspaceSave.isPending && <Loader2 size={11} className="animate-spin" />}
                  {workspaceSave.isPending ? 'Saving…' : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {viewerSha && (sourceIngestionId || sourceUnavailable) && (
        <SourceViewerDialog
          ingestionId={sourceIngestionId ?? ''}
          files={sourceFiles}
          anchor={viewerAnchor}
          unavailableReason={sourceUnavailable ?? undefined}
          initialSha={viewerSha}
          designateFor={designateField}
          onClose={() => {
            setViewerSha(null)
            setViewerAnchor(null)
            setSourceUnavailable(null)
            setDesignateField(null)
          }}
        />
      )}
      {packageOpen
        ? createPortal(
            // Keep the package modal outside the dossier's clipped stacking
            // context. The dossier is an overflow-hidden, backdrop-filtered
            // dialog; nesting a second fixed dialog there made the Package
            // action update state without producing a reachable modal.
            <DealPackageDialog loanId={loanId} onClose={() => setPackageOpen(false)} />,
            document.body,
          )
        : null}
      {confirmOpen ? <ConfirmationViewDialog loanId={loanId} onClose={() => setConfirmOpen(false)} /> : null}
    </div>
     </DossierMetricsProvider>
    </DossierEditProvider>
  )
}

function ScreeningPanel({
  sourceIngestionId,
  screen,
  loading,
}: {
  sourceIngestionId?: string | null
  screen?: import('@/services/ingestion').ScreenResults | null
  loading: boolean
}) {
  if (!sourceIngestionId) {
    return (
      <div className="flex h-48 items-center justify-center text-[12px] text-stone-500">
        No linked intake package — screening runs at ingest time.
      </div>
    )
  }
  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-[12px] text-stone-500">
        <Loader2 size={14} className="animate-spin" /> Loading screening…
      </div>
    )
  }
  if (!screen) {
    return (
      <div className="flex h-48 items-center justify-center text-[12px] text-stone-500">
        No screening results on this package.
      </div>
    )
  }
  if (screen.status === 'not_run') {
    return <ScreeningNotRun screen={screen} />
  }
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-stone-500">
        Credit-box screen results by program
        {screen.programs?.length ? ` (${screen.programs.join(', ')})` : ''}.
      </p>
      <ScreeningRuleset runs={screen.runs ?? []} />
      {(screen.runs ?? []).map((run, idx) => (
        <div key={idx} className="rounded-lg border border-amber-400/15 bg-black/30 px-3 py-2">
          <div className="mb-1 text-[12px] font-medium text-stone-200">
            {run.programName || run.program || `Run ${idx + 1}`}
            {run.outcome && (
              <span className="ml-2 text-[10px] font-normal uppercase text-amber-300">{run.outcome}</span>
            )}
          </div>
          {/* Every check, failing ones first — the rule that made this an
              EXCEPTION is the line a reader came for. */}
          <ScreeningChecks run={run} />
        </div>
      ))}
      {(screen.runs ?? []).length === 0 && (
        <p className="text-[12px] text-stone-500">Screening marked run, but no program results recorded.</p>
      )}
    </div>
  )
}

type FieldRowProps = {
  expandedDetails?: boolean
  def: DealFieldDef
  saved: unknown
  draftValue: unknown
  hasDraft: boolean
  lineage?: LoanFieldLineageField
  /** The intake record's own account of a value the deal row lacks. */
  intake?: LoanFieldLineageField
  derivedNote?: string
  justDerived: boolean
  conflict?: { stated?: unknown; implied?: unknown; computed?: unknown; note?: string }
  onChange: (value: unknown) => void
  /** Picking an address suggestion answers the fields beside it too. */
  onResolvedAddress?: (address: ResolvedAddress) => void
  mapsKey?: string | null
  /** Open the document this value came from, on this page. */
  onOpenSource?: (source: string | null) => void
  /** Open the package to point at where a missing value lives. */
  onFindInSource?: (fieldLabel: string) => void
  /** What the sponsor's own model states for this field, if anything.
   *
   *  Usually the value the field already holds — the sponsor's model fills the
   *  deal's empty fields on arrival, because originations screens on their pro
   *  forma. This is what lets the row SAY so. */
  sponsor?: SponsorFigure
}

/**
 * Which dossier page owns which slice of the field catalogue.
 *
 * The catalogue used to be appended to the bottom of Property, Economics and
 * Parties, beneath the panels those pages are built from. On Economics that
 * put three collapsed strips — 24, 17 and 16 unfilled — under a grid of charts
 * sized to fill the viewport, squashing the page the fields were meant to
 * complete. A field list is not a footer.
 *
 * So no page renders the catalogue any more except Audit, which renders the
 * whole of it, sectioned by the page that owns each part. This map is what
 * says which page that is: a reader after the Economics fields opens
 * Audit -> All fields -> Economics and finds all 62 of them somewhere with
 * room for them, rather than three strips crammed under a chart.
 *
 * Ownership must stay total. A group listed on no page here is a field nobody
 * can see or edit, which is how the retired Record tab lost 34 of them.
 */
export const CATALOGUE_PAGES: { tab: DealDossierTab; label: string; groups: string[] }[] = [
  // Deal Identity, The Request and Workflow have no panel of their own; the
  // Overview workspace is the page that discusses them.
  { tab: 'overview', label: 'Overview', groups: ['Deal Identity', 'The Request', 'Workflow'] },
  { tab: 'property', label: 'Property', groups: ['Property'] },
  {
    tab: 'economics',
    label: 'Economics',
    groups: ['Economics', 'Structure & Pricing', 'Equity & Returns'],
  },
  { tab: 'parties', label: 'Parties', groups: ['Parties'] },
]

/** One shared empty array, so a tab with no catalogue slice does not hand the
 *  memo above a fresh reference on every render. */
const NO_CATALOGUE_GROUPS: string[] = []

/**
 * Which tab renders catalogue fields. Audit, and only Audit — see above.
 *
 * Derived from `CATALOGUE_PAGES` rather than restated, so a group added to a
 * page cannot go missing from the one place that shows every group.
 */
export const CATALOGUE_GROUPS_BY_TAB: Partial<Record<DealDossierTab, string[]>> = {
  audit: CATALOGUE_PAGES.flatMap((page) => page.groups),
}

/**
 * The address under the deal's name.
 *
 * An address nothing supports is not printed: the text on the deal may be a
 * street a name search chose or a city no document states, and printing it is
 * how a deal named Washington Square came to sit at the Washington Monument. In
 * its place the header says so and offers the one action that fixes it. A street
 * that is missing takes only itself away: a city and state the package stated
 * still print, with a note that the street is not.
 */
export function DossierAddressLine({
  loading,
  address,
  notStated,
  streetNotStated = false,
  stateOnly = false,
  canConfirm = true,
  unchecked = false,
  onRetry,
  lat,
  lng,
  onConfirm,
}: {
  loading: boolean
  address: string
  notStated: boolean
  /** A place is stated (city, state) but no street is. */
  streetNotStated?: boolean
  /** The place is a state and nothing else: neither a street nor a city is stated. */
  stateOnly?: boolean
  /** The viewer may edit the deal; a view-only account is told, not offered an editor. */
  canConfirm?: boolean
  /** Where the address parts came from could not be loaded, so nothing vouches for them. */
  unchecked?: boolean
  onRetry?: () => void
  lat: number | null
  lng: number | null
  onConfirm: () => void
}) {
  if (loading) return <span className="min-w-0 break-words">—</span>
  if (unchecked) {
    return (
      <>
        <span data-testid="address-unchecked" className="min-w-0 break-words text-stone-500">
          Address could not be checked
        </span>
        {onRetry ? (
          <button
            type="button"
            data-testid="address-retry"
            onClick={onRetry}
            title="Load where each part of the address came from, then show it"
            className="ml-1 shrink-0 rounded border border-amber-400/40 px-1.5 text-[9px] text-amber-300 hover:bg-amber-400/10"
          >
            retry
          </button>
        ) : null}
      </>
    )
  }
  const confirmButton = canConfirm ? (
    <button
      type="button"
      data-testid="address-confirm"
      onClick={onConfirm}
      title="Enter the street address and confirm it against the package"
      className="ml-1 shrink-0 rounded border border-amber-400/40 px-1.5 text-[9px] text-amber-300 hover:bg-amber-400/10"
    >
      confirm address
    </button>
  ) : null
  if (streetNotStated && address.trim() && !notStated) {
    return (
      <>
        <AddressLink address={address} lat={lat} lng={lng} className="min-w-0 break-words" />
        {stateOnly ? (
          <span data-testid="state-only" className="ml-1.5 shrink-0 text-stone-500">
            · {STATE_ONLY_NOTE.replace(/\.$/, '')}
          </span>
        ) : (
          <span data-testid="street-not-stated" className="ml-1.5 shrink-0 text-stone-500">
            · street not stated in package
          </span>
        )}
        {confirmButton}
      </>
    )
  }
  if (notStated) {
    return (
      <>
        <span data-testid="address-not-stated" className="min-w-0 break-words text-stone-500">
          Address not stated in package
        </span>
        {confirmButton}
      </>
    )
  }
  // The address opens the map at the property.
  return <AddressLink address={address} lat={lat} lng={lng} className="min-w-0 break-words" />
}

export type CataloguePageSlice = {
  label: string
  groups: { group: string; fields: DealFieldDef[] }[]
}

type CataloguePageStats = {
  label: string
  id: string
  total: number
  filled: number
  requiredTotal: number
  requiredMissing: number
  /** Labels of the required fields with no value, for the reader who wants to
   *  know WHICH ones rather than how many. */
  missingRequired: string[]
  /** Of the filled fields, how many hold the sponsor's own figure. */
  fromSponsor: number
}

/** The order states are worth a reader's attention: problems, then people,
 *  then machines, then the absences. Same order the retired Field lineage
 *  page used, for the same reason. */
const CATALOGUE_STATE_ORDER: FieldState[] = [
  'conflicted',
  'confirmed',
  'stated',
  'derived',
  'suggested',
  'in_unread_model',
  'in_unread_attachment',
  'missing',
  'not_applicable',
]

const sectionId = (label: string) =>
  `catalogue-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

/** How much of the catalogue this deal actually holds, and where the holes are.
 *
 *  "117 fields" is not an answer to "is this deal ready". The counts that
 *  matter are how much is filled, how much of what is REQUIRED is missing, and
 *  which page the gaps are on — and each of those has to be one click from the
 *  fields themselves, which is what the jump links are.
 */
function CatalogueCoverage({
  stats,
  byState,
  activeState,
  onFilterState,
  onJump,
}: {
  stats: CataloguePageStats[]
  byState: Map<FieldState, number>
  activeState: FieldState | 'all'
  onFilterState: (state: FieldState | 'all') => void
  onJump: (page: CataloguePageStats) => void
}) {
  const total = stats.reduce((n, s) => n + s.total, 0)
  const filled = stats.reduce((n, s) => n + s.filled, 0)
  const requiredTotal = stats.reduce((n, s) => n + s.requiredTotal, 0)
  const requiredMissing = stats.reduce((n, s) => n + s.requiredMissing, 0)
  const fromSponsor = stats.reduce((n, s) => n + s.fromSponsor, 0)
  const missingRequired = stats.flatMap((s) => s.missingRequired)
  if (total === 0) return null
  const pct = Math.round((filled / total) * 100)
  // Ranked by how thin it is, not by how big: a page 2 fields short of
  // complete is not the one to look at first.
  const thinnest = [...stats]
    .filter((s) => s.total > 0 && s.filled < s.total)
    .sort((a, b) => a.filled / a.total - b.filled / b.total)[0]

  return (
    <div className="mb-3 space-y-2.5 rounded-lg border border-stone-800/70 bg-black/30 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
          Field coverage
        </span>
        <span className="text-[11px] text-stone-400">
          <span className="text-base font-medium text-stone-100">{filled}</span>
          <span className="text-stone-500"> of {total} filled</span>
          <span className="ml-2 text-stone-500">{pct}%</span>
        </span>
      </div>

      <div className="h-1 overflow-hidden rounded-full bg-stone-800">
        <div
          className="h-full rounded-full bg-amber-400/70"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${filled} of ${total} catalog fields filled`}
        />
      </div>

      {/* How each value got here, and how many did. These were a separate
          Field lineage page listing the same fields again; as filters over the
          one list they answer "show me everything a document gave us" without
          asking the reader to hold two lists in their head. */}
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => onFilterState('all')}
          className={
            activeState === 'all'
              ? 'rounded border border-amber-400/50 bg-amber-400/15 px-2 py-0.5 text-[10px] text-amber-100'
              : 'rounded border border-stone-800 px-2 py-0.5 text-[10px] text-stone-400 hover:border-stone-600'
          }
        >
          All {total}
        </button>
        {CATALOGUE_STATE_ORDER.filter((key) => (byState.get(key) ?? 0) > 0).map((key) => {
          const token = FIELD_STATE_TOKENS[key]
          return (
            <button
              key={key}
              type="button"
              title={token.description}
              onClick={() => onFilterState(activeState === key ? 'all' : key)}
              className={
                activeState === key
                  ? 'inline-flex items-center gap-1 rounded border border-amber-400/50 bg-amber-400/15 px-2 py-0.5 text-[10px] text-amber-100'
                  : 'inline-flex items-center gap-1 rounded border border-stone-800 px-2 py-0.5 text-[10px] text-stone-400 hover:border-stone-600'
              }
            >
              <span className={`h-1.5 w-1.5 rounded-full ${token.dot}`} />
              {token.label} {byState.get(key) ?? 0}
            </button>
          )
        })}
      </div>

      {/* Jump links. Each carries its own fill so the choice of where to go is
          made from the same numbers, not after arriving. */}
      <div className="flex flex-wrap gap-1.5">
        {stats.map((page) => {
          const pagePct = page.total === 0 ? 0 : Math.round((page.filled / page.total) * 100)
          return (
            <button
              key={page.label}
              type="button"
              onClick={() => onJump(page)}
              title={`Jump to ${page.label} — ${page.filled} of ${page.total} filled`}
              className="group flex items-center gap-1.5 rounded border border-stone-800 bg-black/40 px-2 py-1 text-[10px] text-stone-400 hover:border-amber-400/40 hover:text-amber-200"
            >
              <span className="font-mono uppercase tracking-wider">{page.label}</span>
              <span className="text-stone-600 group-hover:text-amber-300/70">
                {page.filled}/{page.total}
              </span>
              {page.requiredMissing > 0 && (
                <span className="rounded-sm bg-amber-400/15 px-1 text-[9px] text-amber-300">
                  {page.requiredMissing} req
                </span>
              )}
              <span className="h-1 w-8 overflow-hidden rounded-full bg-stone-800">
                <span
                  className="block h-full rounded-full bg-stone-500 group-hover:bg-amber-400/70"
                  style={{ width: `${pagePct}%` }}
                />
              </span>
            </button>
          )
        })}
      </div>

      <div className="space-y-1 border-t border-stone-800/70 pt-2 text-[10px]">
        {requiredTotal > 0 && (
          <p className={requiredMissing > 0 ? 'text-amber-300/90' : 'text-emerald-300/80'}>
            {requiredMissing > 0
              ? `${requiredMissing} of ${requiredTotal} required fields are missing`
              : `All ${requiredTotal} required fields are answered`}
            {missingRequired.length > 0 && (
              <span className="text-amber-200/60"> — {missingRequired.join(', ')}</span>
            )}
          </p>
        )}
        {fromSponsor > 0 && (
          <p className="text-sky-300/70">
            {fromSponsor} of the {filled} filled values are the sponsor's own
            figures, taken from their model — the client's diligence has not
            been run against them yet.
          </p>
        )}
        {thinnest && (
          <p className="text-stone-500">
            Least complete: <span className="text-stone-300">{thinnest.label}</span>{' '}
            ({Math.round((thinnest.filled / thinnest.total) * 100)}% —{' '}
            {thinnest.total - thinnest.filled} unfilled)
          </p>
        )}
      </div>
    </div>
  )
}

/** The whole catalogue, sectioned by the dossier page that owns each part.
 *
 *  Page first, group second: "where would I look for this on the dossier" is
 *  the question a reader actually arrives with, and it is not the same
 *  question as which catalogue group the backend files a field under.
 */
/**
 * The one grid the catalogue header and every row share. Rows used to declare
 * their own template, so no two columns lined up down the page.
 */
export const CATALOGUE_GRID =
  'grid grid-cols-[minmax(150px,1.2fr)_minmax(120px,1.3fr)_minmax(110px,0.9fr)_minmax(96px,auto)] items-center gap-3'

export function CatalogueSection({
  pages,
  rowProps,
  header,
  offCatalogue = [],
  onOpenSource,
  onFindInSource,
}: {
  pages: CataloguePageSlice[]
  rowProps: (def: DealFieldDef) => FieldRowProps
  header?: ReactNode
  /** Open the document a recorded value came from, on this page. */
  onOpenSource?: (source: string | null) => void
  /** Open the package to point at where a missing value lives. */
  onFindInSource?: (fieldLabel: string) => void
  /** Tracked fields with no catalogue definition — a retired key, or a custom
   *  field added on one deal. The lineage page showed these; the catalogue
   *  cannot, so they get a section of their own rather than disappearing in
   *  the merge. */
  offCatalogue?: LoanFieldLineageField[]
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [stateFilter, setStateFilter] = useState<FieldState | 'all'>('all')
  const [query, setQuery] = useState('')
  const isFilled = (def: DealFieldDef) => {
    const p = rowProps(def)
    const value = p.hasDraft ? p.draftValue : p.saved
    return !(value === null || value === undefined || value === '')
  }
  // The sponsor's figure only where the lineage says the sponsor's document
  // is the source: equal to the model's cell is not enough (a term sheet can
  // state the same loan amount a person then confirmed).
  const isFromSponsor = (def: DealFieldDef) => {
    const p = rowProps(def)
    return holdsSponsorFigure(def, p.hasDraft ? p.draftValue : p.saved, p.sponsor)
      && sponsorAttributionHolds(p.lineage, p.sponsor)
  }
  const stateOf = (def: DealFieldDef) => {
    const p = rowProps(def)
    return p.justDerived ? 'derived' : catalogueState(p.lineage, !isFilled(def), p.intake)
  }
  const matches = (def: DealFieldDef) => {
    if (stateFilter !== 'all' && stateOf(def) !== stateFilter) return false
    const needle = query.trim().toLowerCase()
    if (!needle) return true
    const p = rowProps(def)
    const value = p.hasDraft ? p.draftValue : p.saved
    return (
      def.label.toLowerCase().includes(needle) ||
      def.name.toLowerCase().includes(needle) ||
      String(value ?? '').toLowerCase().includes(needle) ||
      (p.lineage?.source ?? '').toLowerCase().includes(needle) ||
      lineageSummary(p.lineage).toLowerCase().includes(needle)
    )
  }
  if (pages.length === 0) return null

  const stats: CataloguePageStats[] = pages.map((page) => {
    const fields = page.groups.flatMap((g) => g.fields)
    const missingRequired = fields.filter((d) => d.required && !isFilled(d))
    return {
      label: page.label,
      id: sectionId(page.label),
      total: fields.length,
      filled: fields.filter(isFilled).length,
      requiredTotal: fields.filter((d) => d.required).length,
      requiredMissing: missingRequired.length,
      missingRequired: missingRequired.map((d) => d.label),
      fromSponsor: fields.filter(isFromSponsor).length,
    }
  })

  const byState = new Map<FieldState, number>()
  for (const page of pages) {
    for (const group of page.groups) {
      for (const def of group.fields) {
        const key = stateOf(def)
        byState.set(key, (byState.get(key) ?? 0) + 1)
      }
    }
  }

  const shown = pages.map((page) => ({
    label: page.label,
    groups: page.groups
      .map((g) => ({ group: g.group, fields: g.fields.filter(matches) }))
      .filter((g) => g.fields.length > 0),
  }))
  const shownCount = shown.reduce(
    (n, page) => n + page.groups.reduce((m, g) => m + g.fields.length, 0),
    0,
  )
  const totalCount = stats.reduce((n, s) => n + s.total, 0)
  const filtering = stateFilter !== 'all' || query.trim() !== ''

  const jump = (page: CataloguePageStats) => {
    // Open it first: scrolling to a collapsed section lands the reader on a
    // header bar with nothing under it, which reads as a broken link.
    setCollapsed((c) => ({ ...c, [page.label]: false }))
    requestAnimationFrame(() => {
      document.getElementById(page.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  return (
    <div className="space-y-2">
      {header}
      <CatalogueCoverage
        stats={stats}
        byState={byState}
        activeState={stateFilter}
        onFilterState={setStateFilter}
        onJump={jump}
      />

      <label className="flex items-center gap-1.5 rounded border border-stone-800 bg-black/30 px-2 py-1">
        <Search size={11} className="shrink-0 text-stone-600" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by field, value or source"
          aria-label="Search the field catalog"
          className="w-full bg-transparent text-[11px] text-stone-200 placeholder:text-stone-600 focus:outline-none"
        />
      </label>

      {filtering && (
        <div className="text-[9px] text-stone-600">
          Showing {shownCount} of {totalCount} field{totalCount === 1 ? '' : 's'}
          {stateFilter !== 'all' ? ` · ${FIELD_STATE_TOKENS[stateFilter].description}` : ''}
        </div>
      )}
      {filtering && shownCount === 0 && (
        <div className="rounded border border-stone-800 px-3 py-4 text-center text-[11px] text-stone-600">
          No field matches this filter.
        </div>
      )}

      {shown.map(({ label, groups: pageGroups }, index) => {
        const stat = stats[index]
        if (stat.total === 0 || pageGroups.length === 0) return null
        const missing = stat.total - stat.filled
        const isCollapsed = collapsed[label] ?? false
        return (
          <section
            key={label}
            id={stat.id}
            className="scroll-mt-2 rounded-lg border border-stone-800/70 bg-black/20"
          >
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [label]: !isCollapsed }))}
              aria-expanded={!isCollapsed}
              className="flex w-full items-center justify-between px-3 py-2 text-left"
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-stone-400">
                {label}
              </span>
              <span className="flex items-center gap-2 text-[9px] text-stone-600">
                {stat.filled}/{stat.total}
                <span className={missing > 0 ? 'text-amber-500/70' : 'text-stone-600'}>
                  {missing > 0 ? `${missing} unfilled` : 'complete'}
                </span>
                {isCollapsed ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
              </span>
            </button>
            {isCollapsed ? null : (
              <div className="border-t border-stone-800/70 px-2 py-1.5">
                <div
                  className={`${CATALOGUE_GRID} border-b border-stone-800/70 px-2 pb-1 text-[9px] font-medium uppercase tracking-wider text-stone-600`}
                >
                  <span>Field</span>
                  <span>Value</span>
                  <span>How it was obtained</span>
                  <span className="text-right">Source</span>
                </div>
                {pageGroups.map(({ group, fields: groupFields }) => (
                  <div key={group}>
                    {/* A sub-heading only where it says something the page
                        heading did not. Economics carries three groups;
                        Property is one group of the same name. */}
                    {group === label ? null : (
                      <div className="px-1 pb-0.5 pt-2 font-mono text-[9px] uppercase tracking-widest text-stone-600">
                        {group}
                      </div>
                    )}
                    {groupFields.map((def) => (
                      <FieldRow
                        key={def.name}
                        {...rowProps(def)}
                        onOpenSource={onOpenSource}
                        onFindInSource={onFindInSource}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </section>
        )
      })}

      {offCatalogue.length > 0 && (
        <section className="rounded-lg border border-stone-800/70 bg-black/20">
          <div className="px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-stone-400">
              Off catalog
            </div>
            <p className="mt-0.5 text-[9px] text-stone-600">
              Tracked on this deal but not part of the field catalog, so no page
              owns them. Read-only here.
            </p>
          </div>
          <ul className="border-t border-stone-800/70 px-2 py-1.5">
            {offCatalogue.map((field) => {
              const held = heldValue(field)
              return (
                <li
                  key={field.fieldName}
                  className={`${CATALOGUE_GRID} px-2 py-1.5 text-[11px]`}
                >
                  <span className="truncate text-stone-300" title={field.fieldName}>
                    {field.fieldName}
                  </span>
                  <span className="truncate font-mono text-stone-400">
                    {displayValue(held)}
                  </span>
                  <FieldStateBadge
                    state={catalogueState(field, held === null || held === undefined || held === '')}
                    title={lineageSummary(field) || null}
                  />
                  <span className="text-right text-[9.5px] text-stone-700">—</span>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}

function FieldRow({
  def,
  saved,
  draftValue,
  hasDraft,
  lineage,
  intake,
  derivedNote,
  justDerived,
  conflict,
  onChange,
  onResolvedAddress,
  mapsKey,
  sponsor,
  onOpenSource,
  onFindInSource,
  expandedDetails = false,
}: FieldRowProps) {
  const { unlocked } = useDossierEdit()
  // What is being typed is left as typed: a thousands separator appearing
  // mid-keystroke fights whoever is entering the number. Unlocked, the box
  // reads the value the way a save writes it ($130,329,000, 65%); locked it
  // reads as the rest of the dossier does. A date is the same day either way.
  const [editing, setEditing] = useState(false)
  const [showLineage, setShowLineage] = useState(expandedDetails)
  const value = hasDraft ? draftValue : saved
  const isEmpty = value === null || value === undefined || value === ''
  const state = catalogueState(lineage, isEmpty, intake)
  const summary = lineageSummary(lineage) || lineageSummary(intake)
  // The file to open: the stated value's document, or — on a lineage row
  // from before the agreed states — the source it names.
  const sourceDocument =
    state === 'stated'
      ? lineage?.provenance?.document ?? (lineage && !lineage.state ? lineage.source : null)
      : null
  // Equal to the sponsor model's cell AND the sponsor's statement by the
  // lineage. A value a person confirmed from the countersigned term sheet or
  // the appraisal is not the sponsor's figure because the model agrees.
  const matchesSponsor = holdsSponsorFigure(def, value, sponsor)
  const fromSponsor = matchesSponsor && sponsorAttributionHolds(lineage, sponsor)
  const inputId = `deal-field-${def.name}`
  // R1: people resolve inputs, not outputs. A value the engine derived is
  // not a box to type over — the server refuses that write (nd-011) — so the
  // row stays read-only when unlocked and names what to correct instead.
  // A derived date is the exception: typing a maturity states it, and a stated one is never replaced.
  const derivedOutput = !justDerived && state === 'derived' && !isDerivedDateField(def.name)
  const editable = unlocked && !derivedOutput
  const derivedTitle = derivedOutput
    ? `${def.label} is derived by the platform${
        lineage?.derivation?.formula ? ` (${lineage.derivation.formula})` : ''
      }. To change it, correct its inputs; it recomputes from them.`
    : undefined

  return (
    <div
      className={`${CATALOGUE_GRID} border-t border-white/[0.03] px-2 py-1.5 first:border-t-0 ${
        conflict ? 'bg-amber-500/5 ring-1 ring-inset ring-amber-500/25' : hasDraft ? 'bg-amber-400/5' : ''
      }`}
    >
      <label htmlFor={inputId} className="min-w-0">
        <span className="block truncate text-[12px] text-stone-300">
          {def.label}
          {def.required ? <span className="text-red-400/80"> *</span> : null}
        </span>
        {!justDerived && (derivedNote || def.accelerator || def.help) && (
          <span className="block truncate text-[9.5px] text-stone-600">
            {derivedNote ? `↻ ${derivedNote}` : def.help ?? 'feeds dependent fields'}
          </span>
        )}
      </label>

      <div className="flex items-center gap-1.5">
        {def.kind === 'select' ? (
          <select
            id={inputId}
            value={displayValue(value)}
            disabled={!editable}
            title={derivedTitle}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full rounded border border-stone-700 bg-[#0d0d0d] px-2 py-1 text-[12px] text-stone-200 focus:border-amber-400/50 focus:outline-none disabled:cursor-default ${
              editable ? 'dossier-edit-hairline' : ''
            }`}
          >
            <option value="">—</option>
            {def.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : def.name === 'street' && editable ? (
          // The one field on the catalogue worth completing: picking a
          // suggestion fills the city, state and postal code beside it, which
          // is four boxes answered by one, and the postal code is the one most
          // often left blank.
          <AddressAutocomplete
            id={inputId}
            value={displayValue(value)}
            onChange={onChange}
            onResolved={onResolvedAddress}
            apiKey={mapsKey ?? null}
            placeholder={isEmpty ? 'Not recorded' : undefined}
            className="dossier-edit-hairline border-stone-700 bg-[#0d0d0d] px-2 py-1 focus:border-amber-400/50"
          />
        ) : (
          <input
            id={inputId}
            type={def.kind === 'date' && (isEmpty || dateInputValue(value) !== null) ? 'date' : 'text'}
            inputMode={
              def.kind === 'currency' || def.kind === 'percent' || def.kind === 'number'
                ? 'decimal'
                : undefined
            }
            value={
              editable || def.kind === 'date'
                ? editableValue(def, value, { typing: editing })
                : formatFieldValue(value, def.kind, def.name)
            }
            readOnly={!editable}
            title={derivedTitle}
            onFocus={() => setEditing(true)}
            onBlur={() => setEditing(false)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={
              isEmpty
                ? sponsor
                  ? `${formatFieldValue(sponsor.value, def.kind, def.name)} per the sponsor`
                  : 'Not recorded'
                : undefined
            }
            className={`w-full rounded border bg-[#0d0d0d] px-2 py-1 text-[12px] placeholder:text-stone-700 focus:outline-none ${
              justDerived
                ? 'border-violet-500/40 text-stone-200 focus:border-violet-400/60'
                : isEmpty && sponsor
                  ? 'border-sky-500/30 text-stone-200 placeholder:text-sky-300/60 focus:border-amber-400/50'
                  : 'border-stone-700 text-stone-200 focus:border-amber-400/50'
            } ${editable ? 'dossier-edit-hairline' : ''}`}
          />
        )}
        {justDerived && <RecalculationNote label={def.label} explanation={derivedNote ?? "Recalculated from dependent deal fields."} />}
        {/* A field the fill skipped — the model states it but the deal was
            already answered, or the figure was refused at the column's
            acceptance. Offered here rather than lost. */}
        {isEmpty && sponsor && unlocked && (
          <button
            type="button"
            onClick={() => onChange(sponsor.value)}
            title={`Take ${sponsor.value} from ${sponsor.filename} ${sponsor.sheet}!${sponsor.cell}`}
            className="flex-shrink-0 whitespace-nowrap rounded border border-sky-500/30 px-1.5 py-1 text-[9px] text-sky-300 hover:bg-sky-500/10"
          >
            Use sponsor's
          </button>
        )}
        {def.unit && <span className="w-8 flex-shrink-0 text-[10px] text-stone-600">{def.unit}</span>}
      </div>

      <div className="flex items-center gap-1">
        {/* Whose number this is, on the row itself. The sponsor's model fills
            the deal's empty fields because the client screens on their pro
            forma and does its own diligence after — which is only safe while
            every one of those figures says it is the sponsor's. */}
        {fromSponsor && (
          <span
            title={`Stated in ${sponsor?.filename} at ${sponsor?.sheet}!${sponsor?.cell}`}
            className="rounded-sm bg-sky-500/10 px-1 py-0.5 text-[8px] font-medium uppercase tracking-wider text-sky-300 ring-1 ring-inset ring-sky-500/25"
          >
            Sponsor
          </span>
        )}
        <FieldStateBadge
          state={justDerived ? 'derived' : state}
          formula={justDerived ? derivedNote : lineage?.derivation?.formula}
          title={justDerived || !summary ? null : `${FIELD_STATE_TOKENS[state].label}: ${summary}`}
        />
        <button
          type="button"
          onClick={() => setShowLineage((o) => !o)}
          disabled={!lineage}
          title={lineage ? 'Where this value came from' : 'Nothing is recorded about this value'}
          aria-expanded={showLineage}
          aria-label={`Provenance for ${def.label}`}
          className="rounded p-0.5 text-stone-600 hover:text-amber-300 disabled:invisible"
        >
          <ChevronRight size={11} className={showLineage ? 'rotate-90 transition-transform' : 'transition-transform'} />
        </button>
      </div>

      {/* The source, reachable. Naming the document told a reader which file to
          go and open somewhere else; this opens it here. A missing value gets
          the package in designate mode instead, so the answer is given where
          the evidence is. */}
      <div className="flex min-w-0 justify-end">
        {sourceDocument && !isEmpty && onOpenSource ? (
          <button
            type="button"
            onClick={() => onOpenSource(sourceDocument)}
            title={`Open ${sourceDocument}`}
            className="inline-flex min-w-0 items-center gap-1 rounded border border-stone-700 px-1.5 py-0.5 text-[9.5px] text-stone-400 hover:border-amber-400/40 hover:text-amber-200"
          >
            <FileText size={9} className="shrink-0" />
            <span className="max-w-[92px] truncate">{sourceDocument}</span>
          </button>
        ) : isEmpty && onFindInSource ? (
          <button
            type="button"
            onClick={() => onFindInSource(def.label)}
            title={`Open the package and point at ${def.label}`}
            className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-400/25 bg-amber-400/[0.07] px-1.5 py-0.5 text-[9.5px] text-amber-200/90 hover:border-amber-400/50"
          >
            <Crosshair size={9} className="shrink-0" />
            Find in source
          </button>
        ) : summary && !isEmpty ? (
          <span className="max-w-[92px] truncate text-[9.5px] text-stone-600" title={summary}>
            {summary}
          </span>
        ) : (
          <span className="text-[9.5px] text-stone-700">—</span>
        )}
      </div>

      {sponsor && matchesSponsor && !fromSponsor && !isEmpty && (
        <div className="col-span-4 -mt-0.5 mb-1 pl-2 text-[9px] text-stone-500">
          The sponsor&rsquo;s model also states this figure, at {sponsor.filename} {sponsor.sheet}!{sponsor.cell}.
          The value on the deal is not theirs.
        </div>
      )}

      {sponsor && (fromSponsor || isEmpty) && (
        <div className="col-span-4 -mt-0.5 mb-1 pl-2 text-[9px] text-sky-300/50">
          {fromSponsor ? 'The sponsor’s figure' : 'Stated by the sponsor'}, from{' '}
          {sponsor.filename} at {sponsor.sheet}!{sponsor.cell}
          {sponsor.label ? ` (“${sponsor.label}”)` : ''}.
          {(sponsor.alternatives ?? []).map((alt) => (
            <span key={`${alt.sheet}!${alt.cell}`}>
              {' '}Also stated as {formatFieldValue(alt.value, def.kind, def.name)} at{' '}
              {alt.sheet}!{alt.cell}.
            </span>
          ))}
        </div>
      )}

      {showLineage && lineage && (
        <div className="col-span-4 mb-1 ml-2 border-l border-amber-400/20 pl-3 text-[10px] text-stone-500">
          <div>
            {summary || '—'}
            {lineage.editorEmail ? ` · ${lineage.editorEmail}` : ''}
            {lineage.updatedAt ? ` · ${fieldStamp(lineage.updatedAt)}` : ''}
            {lineage.confidence != null ? ` · confidence ${Math.round(lineage.confidence * 100)}%` : ''}
          </div>
          {(lineage.history ?? []).length === 0 ? (
            <div className="text-stone-600">No change history is recorded for this field.</div>
          ) : (
            <ol className="mt-0.5 space-y-0.5">
              {(lineage.history ?? []).map((event) => (
                <li key={event.id}>
                  <span className="font-mono text-stone-400">{displayValue(event.oldValue)}</span>
                  <span className="mx-1 text-stone-700">-&gt;</span>
                  <span className="font-mono text-stone-300">{displayValue(event.newValue)}</span>
                  <span className="text-[9px] text-stone-600">
                    {' '}{fieldStamp(event.createdAt)}
                    {event.editorEmail ? ` · ${event.editorEmail}` : ''}
                    {event.source ? ` · ${event.source}` : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}

/** The row's one-line source: the lineage's own summary, the intake record's,
 *  or the formula of an unsaved recalculation. */
function catalogueSource(props: FieldRowProps, state: FieldState): string {
  if (props.justDerived) return props.derivedNote ? `Derived: ${props.derivedNote}` : 'Derived from your edits'
  const own = lineageSummary(props.lineage)
  const ownState = stateOfLineage(props.lineage, 'missing')
  if (own && ownState !== 'missing' && ownState !== 'not_applicable') return own
  if (props.intake && state !== 'missing') return lineageSummary(props.intake)
  // Absence states carry no figure to source: N/A, a blank row, a conflict
  // still being resolved, a value only an unread model holds.
  if (state !== 'stated' && state !== 'derived' && state !== 'suggested' && state !== 'confirmed') {
    return '—'
  }
  return own || 'Suggested: no source is on file — confirm it'
}

/** A timestamp a reader can place, or the raw string if it will not parse. */
function fieldStamp(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? String(iso) : date.toLocaleString()
}

/** Host mounted once in Layout — driven by dealDossierStore. */
export function DealDossierHost() {
  const { isOpen, loanId, tab, loanSnapshot, closeDossier, setTab } = useDealDossierStore()
  const location = useLocation()
  const openDossier = useDealDossierStore(state => state.openDossier)
  useEffect(() => {
    const target = dossierLinkTarget(location.search)
    if (target) openDossier(target.loanId, target.tab)
  }, [location.search, openDossier])
  if (!isOpen || !loanId) return null
  return (
    <DealDossier
      loanId={loanId}
      fallbackTitle={loanSnapshot?.property}
      tab={tab}
      onTabChange={setTab}
      onClose={closeDossier}
    />
  )
}

export default DealDossier
